import { useEffect, useRef, useState, useCallback } from 'react';
import { Canvas, Line, ActiveSelection, FabricImage, Polygon, Text } from 'fabric';
import GridOverlay from './GridOverlay';
import { Maximize2 } from 'lucide-react';
import { useGrid } from '../../context/GridContext';
import { useGardenSettings } from '../../context/GardenSettingsContext';
import { useCommand } from '../../context/CommandContext';
import { usePlant } from '../../context/PlantContext';
import { useLayer } from '../../context/LayerContext';
import { imagesApi } from '../../services/api';
import GridControls from './GridControls';
import useCanvasTool from '../../hooks/useCanvasTool';
import useCanvasPerformanceMonitor from '../../hooks/useCanvasPerformanceMonitor';
import DimensionTooltip from './DimensionTooltip';
import PlantHoverTooltip from './PlantHoverTooltip';
import OverlapHintTooltip from './OverlapHintTooltip';
import ConfirmDialog from '../ui/ConfirmDialog';
import CanvasContextMenu from './CanvasContextMenu';
import useContextMenuActions from '../../hooks/useContextMenuActions';
import { calculatePolygonCentroid, formatZoneLabel, zoneStrokeDashArray } from './tools/HeightZoneTool';
import { CreateObjectCommand } from '../../commands/CreateObjectCommand';
import { DeleteObjectCommand } from '../../commands/DeleteObjectCommand';
import { TransformObjectCommand } from '../../commands/TransformObjectCommand';
import { CompoundCommand } from '../../commands/CompoundCommand';
import { GroupObjectsCommand } from '../../commands/GroupObjectsCommand';
import { UngroupObjectsCommand } from '../../commands/UngroupObjectsCommand';
import { isUserGroup, enterEditMode, exitEditMode } from './utils/groupUtils';
import { applyCanvasObjects } from './utils/applyCanvasObjects';
import { isPersistableObject } from './utils/persistableObjects';
import { resolveScalingTransform, clampLineEndpoint } from './utils/scaleConstraints';
import { attachPinchZoom } from '../../utils/canvasTouchUtils';
import { getDefaultStyle } from '../../config/gardenObjectTypes';
import {
  calculateRectangleBounds,
  createRectangle,
  updateRectangleAppearance,
} from './tools/RectangleTool';
import {
  calculateEllipseRadii,
  createEllipse,
  updateEllipseAppearance,
} from './tools/CircleTool';
import {
  calculateLineCoordinates,
  calculateLineProperties,
  createLine,
  updateLineAppearance,
  pixelsToCm,
} from './tools/LineTool';
import { updatePolygonAppearance } from './tools/PolygonTool';
import { updateBezierAppearance } from './tools/BezierTool';
import { isVertexHandle } from './tools/vertexEditUtils';
import { useVertexEditing } from '../../hooks/useVertexEditing';
import { useBezierVertexEditing } from '../../hooks/useBezierVertexEditing';
import { zoomToFitGarden } from '../../utils/canvasZoomUtils';
import PolygonDrawingTool from './tools/PolygonDrawingTool';
import BezierDrawingTool from './tools/BezierDrawingTool';
import CenterlineDrawingTool from './tools/CenterlineDrawingTool';
import GatePlacementTool from './tools/GatePlacementTool';
import {
  GATE_ATTACH_THRESHOLD,
  GATE_DETACH_THRESHOLD,
  isOpening,
  getOpeningTypeId,
  parentsForOpening,
  isLinkableParent,
  ensureObjectId,
  findLinkableParents,
  findNearestEdgePoint,
  clampGateT,
  pointOnEdgeAt,
  setObjectCenterAt,
  applyInheritedHeight,
  syncLinkedGates,
  reprojectLinkedGates,
  detachGatesFromShortEdges,
  normalizeLineRawCoords,
} from './tools/gateLinkUtils';
import CompassWidget from './CompassWidget';
import SlopeIndicator from './SlopeIndicator';
import PlantPlacementManager from './PlantPlacementManager';
import PlantDragHandler from './PlantDragHandler';
import { imageScaleForCanonicalDisplay } from '../Wizard/inputs/scaleCalibrationUtils';
import { getLayerForObject, applyLayerOrder } from '../../constants/layerDefinitions';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { attachMeasurementLabelScaling } from '../../utils/measurementLabelScaling';

// ---------------------------------------------------------------------------
// Group serialization helpers (module-level, no React dependencies)
// ---------------------------------------------------------------------------

/**
 * Recursively serializes a user group including proper PlantCircle sub-object data.
 * @param {fabric.Group} group
 * @returns {Object} plain JSON
 */
function serializeGroupObject(group) {
  const groupJson = group.toJSON(['data', 'objectType', 'renderOptions']);
  groupJson.data = group.data ? { ...group.data } : {};
  groupJson.objectType = 'group';

  // Override objects array to ensure PlantCircle data is fully captured
  groupJson.objects = group.getObjects().map(subObj => {
    if (subObj.objectType === 'plant') {
      // PlantCircle.toObject() includes plantProperties
      return subObj.toObject();
    }
    if (subObj.type === 'group' && subObj.objectType !== 'plant') {
      return serializeGroupObject(subObj); // Nested group
    }
    const subJson = subObj.toJSON(['data', 'objectType', 'renderOptions']);
    if (subObj.data) subJson.data = subObj.data;
    if (subObj.objectType) subJson.objectType = subObj.objectType;
    if (subObj.renderOptions) subJson.renderOptions = subObj.renderOptions;
    return subJson;
  });

  return groupJson;
}

/**
 * GardenCanvas - Main 2D garden editor canvas component
 *
 * Features:
 * - Fabric.js canvas initialization
 * - Zoom and pan controls
 * - Responsive canvas sizing
 * - Dynamic grid background with configurable size
 * - Snap-to-grid with Ctrl key override
 * - Visual snap feedback
 * - Touch and mouse event handling
 * - Rectangle drawing tool with modifiers
 */
const GardenCanvas = ({
  width = 800,
  height = 600,
  onCanvasReady,
  activeTool = 'select',
  onToolChange,
  onSelectionChange,
  onObjectDelete,
  activeTab = 'zeichnen',
  selectedObjectType = null,
  externalCanvasData = null,
  externalCanvasNonce = 0,
  onCanvasDirty,
  onUnmountFlush,
  readOnly = false,
  viewMode = '2d',
}) => {
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  // The mount effect's cleanup captures first-render props ([] deps), so the
  // flush callback is reached through a ref that always holds the latest one.
  const onUnmountFlushRef = useRef(onUnmountFlush);
  useEffect(() => { onUnmountFlushRef.current = onUnmountFlush; }, [onUnmountFlush]);
  const containerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  // Mirror of fabricCanvasRef as state, set once the canvas is created. Child
  // components and the context menu read this (not fabricCanvasRef.current) so the
  // canvas is never accessed via a ref during render (react-hooks/refs).
  const [fabricCanvas, setFabricCanvas] = useState(null);
  const snapGuidesRef = useRef([]);
  const [mousePosition, setMousePosition] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  // The object pending deletion lives in state (not a ref) because the confirm
  // dialog renders its type/count.
  const [objectToDelete, setObjectToDelete] = useState(null);
  const saveTimeoutRef = useRef(null);
  const transformStartStateRef = useRef(null); // Track transform start state for undo/redo
  const previouslySelectedObjectsRef = useRef([]); // Track previously selected objects for deselection
  const editingGroupRef = useRef(null); // Track active group in edit mode
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, target: null });

  // Get grid settings from context
  const { gridEnabled, snapEnabled, gridSize, gridColor, gridOpacity } = useGrid();

  // Get garden settings (includes wizard data)
  const { settings, updateSettings } = useGardenSettings();

  // Fixed-positionierte Overlays (Kompass, Zoom-Controls) sind am rechten
  // Desktop-Properties-Panel (300 px) ausgerichtet; auf Tablet (Panel 0 px)
  // und Mobile gehören sie an den Viewport-Rand.
  const { isMobile, isDesktop } = useBreakpoint();
  const overlayRight = isDesktop ? '316px' : '16px';

  // North-view: rotate canvas so that north points up
  const [northViewActive, setNorthViewActive] = useState(false);

  // Plant hover tooltip state
  const [hoveredPlant, setHoveredPlant] = useState(null);
  const hoverTimeoutRef = useRef(null);

  // Overlap hint tooltip state (brief overlay on state change)
  const [overlapHint, setOverlapHint] = useState(null);


  // Get command stack for undo/redo
  const { commandStack } = useCommand();

  // Get plant placement mode state
  const { placementMode } = usePlant();

  // Get layer context for layer management
  const { layerVisibility, layerLocked, objectViewState, isObjectHidden, isLayerLocked, isObjectLocked } = useLayer();

  // Latest lock lookup, held in a ref so applyTabObjectBehavior (a []-deps
  // useCallback) can consult the current layer/object lock state without
  // churning its identity. Updated after every render; the tab-behavior effect
  // re-runs whenever layerLocked/objectViewState change (see below), so a lock
  // toggle re-applies with the freshest lookup. (#761)
  const isObjectInteractionLockedRef = useRef(() => false);
  useEffect(() => {
    isObjectInteractionLockedRef.current = (obj) =>
      Boolean(isLayerLocked(getLayerForObject(obj)) || isObjectLocked(obj));
  });

  // Tool manager hook
  const tool = useCanvasTool(activeTool);

  // Active-tool ref so closures inside setupZoomAndPan (initialized once) can read the
  // current tool without re-running setup. Required for the pan-tool branch in mouse:down.
  const activeToolRef = useRef(activeTool);
  useEffect(() => {
    activeToolRef.current = activeTool;
  }, [activeTool]);

  // Placement-mode ref so the once-initialized pan closures in setupZoomAndPan can
  // read it without re-running setup. While placing a plant the active tool stays
  // 'select', so the single-finger-pan gate would otherwise steal the placement tap
  // and pan the canvas instead of placing ("alles verschoben", #748).
  const placementModeRef = useRef(placementMode);
  useEffect(() => {
    placementModeRef.current = placementMode;
  }, [placementMode]);

  // Context menu actions (copy, paste, duplicate, reorder, group/ungroup, delete)
  const contextMenuActions = useContextMenuActions(fabricCanvasRef, commandStack);

  // Snap settings ref — kept fresh for vertex editing hook (reads at event time)
  const snapSettingsRef = useRef({ snapEnabled, gridSize });
  useEffect(() => {
    snapSettingsRef.current = { snapEnabled, gridSize };
  }, [snapEnabled, gridSize]);

  // Vertex edit mode (double-click polygon → drag vertex handles)
  const {
    editingPolygonRef,
    enterEditMode: enterVertexEditMode,
    exitEditMode: exitVertexEditMode,
  } = useVertexEditing(fabricCanvasRef, commandStack, snapSettingsRef);

  // Bezier vertex edit mode (double-click bezier path → drag anchor handles)
  const {
    editingPathRef,
    enterEditMode: enterBezierVertexEditMode,
    exitEditMode: exitBezierVertexEditMode,
  } = useBezierVertexEditing(fabricCanvasRef, commandStack, snapSettingsRef);

  // Performance monitor (dev-only — all methods are no-ops in production)
  // Access in browser DevTools console via: window.perfMonitor.printReport()
  const perfMonitor = useCanvasPerformanceMonitor(
    fabricCanvas,
    'GardenCanvas'
  );
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    window.perfMonitor = perfMonitor;
    return () => { delete window.perfMonitor; };
  }, [perfMonitor]);

  // Wire up performance tracking for mouse:move events (dev-only)
  useEffect(() => {
    if (!isReady || !fabricCanvasRef.current) return;
    const canvas = fabricCanvasRef.current;

    const handlePerfMouseMove = () => { perfMonitor.trackMouseMove(); };
    canvas.on('mouse:move', handlePerfMouseMove);

    return () => { canvas.off('mouse:move', handlePerfMouseMove); };
  }, [isReady, perfMonitor]);

  /**
   * Calculate scale factors between wizard canvas and current canvas
   * NOTE: Wizard objects should maintain their original size (960×600) regardless of viewport changes.
   * We use 1:1 scaling to keep alignment with background image.
   */
  const calculateScaleFactors = useCallback((_wizardCanvasSize) => {
    // Always use 1:1 scaling - wizard objects keep their original size
    // Users can zoom/pan to view the full canvas if it's larger than viewport
    return { scaleX: 1, scaleY: 1 };
  }, []);

  /**
   * Scale a point from wizard canvas to current canvas
   */
  const scalePoint = useCallback((point, scaleFactors) => {
    return {
      x: point.x * scaleFactors.scaleX,
      y: point.y * scaleFactors.scaleY
    };
  }, []);

  /**
   * Enforce layer order on canvas
   *
   * Sorts all canvas objects by their layer order (Z-Index).
   * Grid lines stay at the bottom, then objects are sorted by layer,
   * preserving order within the same layer (stable sort).
   *
   * Called after:
   * - object:added events
   * - Canvas load from localStorage
   * - Wizard data load
   */
  const enforceLayerOrder = useCallback((canvas) => {
    applyLayerOrder(canvas);
    if (canvas) canvas.renderAll();
  }, []);

  /**
   * Assign layer to object if not already set.
   * When canvas is provided, also assigns zIndexWithinLayer so new objects
   * appear on top of existing objects in the same layer.
   *
   * @param {Object} fabricObject - Fabric.js object
   * @param {fabric.Canvas|null} [canvas] - Canvas for zIndexWithinLayer calculation
   */
  const assignLayerToObject = useCallback((fabricObject, canvas = null) => {
    if (!fabricObject) return;

    // Skip transient ghosts (e.g. placement preview circles, snap guides).
    // Tagging them would make the TabBar counter mistake them for user objects
    // and cause a render loop on placement (#429).
    if (fabricObject.data?.isGhost || fabricObject.data?.isSnapGuide) return;

    // Skip if already has layer assigned
    if (fabricObject.data?.layer) return;

    // Determine layer
    const layerId = getLayerForObject(fabricObject);

    // Assign to object data
    if (!fabricObject.data) {
      fabricObject.data = {};
    }
    fabricObject.data.layer = layerId;

    // Assign zIndexWithinLayer so new objects appear on top within their layer
    if (canvas && fabricObject.data.zIndexWithinLayer === undefined) {
      const objectsInLayer = canvas.getObjects().filter(
        o => !o.data?.isGridLine && !o.data?.isSnapGuide && o.data?.layer === layerId
      );
      const maxZ = objectsInLayer.reduce(
        (max, o) => Math.max(max, o.data?.zIndexWithinLayer ?? 0),
        -1
      );
      fabricObject.data.zIndexWithinLayer = maxZ + 1;
    }
  }, []);

  /**
   * Load background image from wizard data
   */
  const loadBackgroundImage = useCallback(async (canvas, bgImageData) => {
    // Support imageUrl (from backend) or dataUrl (local preview)
    const imageSource = bgImageData?.imageUrl
      ? imagesApi.getFullUrl(bgImageData.imageUrl)
      : bgImageData?.dataUrl;
    if (!imageSource) return null;

    try {
      const fabricImage = await FabricImage.fromURL(imageSource, {
        crossOrigin: 'anonymous',
      });

      // Calculate scale factors from wizard canvas to current canvas
      const scaleFactors = calculateScaleFactors(bgImageData.canvasSize);

      // Scale position from wizard canvas coordinates
      const position = bgImageData.position || { x: 20, y: 20 };
      const scaledPosition = scalePoint(position, scaleFactors);

      // Prefer canonical scaling (1 m on image = 100 px on canvas) when the
      // wizard captured nativePixelsPerMeter. Falls back to fit-to-canvas for
      // legacy gardens calibrated before this field existed.
      const nativePxPerM = bgImageData.calibration?.nativePixelsPerMeter;
      const maxWidth = 920 * scaleFactors.scaleX;
      const maxHeight = 560 * scaleFactors.scaleY;
      const fitScale = Math.min(maxWidth / fabricImage.width, maxHeight / fabricImage.height);
      const imageScale = nativePxPerM ? imageScaleForCanonicalDisplay(nativePxPerM) : fitScale;

      // Apply wizard configuration with scaled values
      fabricImage.set({
        left: scaledPosition.x,
        top: scaledPosition.y,
        scaleX: imageScale,
        scaleY: imageScale,
        angle: bgImageData.rotation || 0,
        opacity: bgImageData.transparency / 100,
        selectable: false,    // Locked
        evented: false,       // Locked
        hasControls: false,
        hasBorders: false,
        originX: 'left',
        originY: 'top',
        objectCaching: true,
        noScaleCache: true,   // Static background; skip cache regeneration on viewport zoom
        data: {
          type: 'wizard-background',
          locked: true
        }
      });

      canvas.add(fabricImage);
      canvas.sendObjectToBack(fabricImage);

      return fabricImage;
    } catch (error) {
      console.error('Failed to load background image:', error);
      return null;
    }
  }, [calculateScaleFactors, scalePoint]);

  /**
   * Load garden boundary polygon from wizard data
   */
  const loadGardenBoundary = useCallback((canvas, outlineData) => {
    if (!outlineData?.vertices || outlineData.vertices.length < 3) return null;

    // Calculate scale factors from wizard canvas to current canvas
    const scaleFactors = calculateScaleFactors(outlineData.canvasSize);

    // Scale all vertices
    const scaledVertices = outlineData.vertices.map(vertex =>
      scalePoint(vertex, scaleFactors)
    );

    const polygon = new Polygon(scaledVertices, {
      originX: 'left',
      originY: 'top',
      fill: 'transparent',
      stroke: '#10B981',      // Emerald-500
      strokeWidth: 2,
      selectable: false,      // Locked
      evented: false,         // Locked
      hasControls: false,
      hasBorders: false,
      objectCaching: true,
      noScaleCache: true,     // Static boundary; skip cache regeneration on viewport zoom
      data: {
        type: 'garden-boundary',
        locked: true
      }
    });

    canvas.add(polygon);
    // Position above background, below user objects

    return polygon;
  }, [calculateScaleFactors, scalePoint]);

  /**
   * Load height zones from wizard data
   */
  const loadHeightZones = useCallback((canvas, heightDiffData) => {
    if (!heightDiffData?.enabled || !heightDiffData.zones) return [];

    // Calculate scale factors from wizard canvas to current canvas
    const scaleFactors = calculateScaleFactors(heightDiffData.canvasSize);

    const zoneObjects = [];

    heightDiffData.zones.forEach((zone) => {
      // Scale zone vertices
      const scaledVertices = zone.vertices.map(vertex =>
        scalePoint(vertex, scaleFactors)
      );

      // Create polygon for zone
      const polygon = new Polygon(scaledVertices, {
        originX: 'left',
        originY: 'top',
        fill: zone.color,
        opacity: 0.3,
        stroke: zone.color,
        strokeWidth: 1,
        // Böschungs-Zonen (#625): gestrichelte Kontur
        strokeDashArray: zoneStrokeDashArray(zone.kind),
        selectable: false,
        evented: false,
        objectCaching: true,
        noScaleCache: true,   // Static zone; skip cache regeneration on viewport zoom
        data: {
          type: 'height-zone',
          zoneId: zone.id,
          height: zone.height,
          locked: true
        }
      });

      canvas.add(polygon);

      // Add height label at centroid (calculate from scaled vertices)
      const centroid = calculatePolygonCentroid(scaledVertices);
      const label = new Text(formatZoneLabel(zone), {
        left: centroid.x,
        top: centroid.y,
        fontSize: 14,
        fontWeight: 'bold',
        fontFamily: 'Inter, sans-serif',
        fill: zone.color,
        backgroundColor: '#FFFFFF',
        padding: 4,
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
        objectCaching: true,
        noScaleCache: true,   // Static label; skip cache regeneration on viewport zoom
        data: {
          type: 'height-zone-label',
          zoneId: zone.id,
          locked: true
        }
      });

      canvas.add(label);

      zoneObjects.push({ polygon, label });
    });

    return zoneObjects;
  }, [calculateScaleFactors, scalePoint]);

  /**
   * Load wizard data into canvas (runs once after canvas is ready)
   */
  const loadWizardData = useCallback(async () => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;

    // Disable auto-render while loading multiple objects
    canvas.renderOnAddRemove = false;

    // 1. Load background image (if exists)
    const bgImage = await loadBackgroundImage(canvas, settings.backgroundImage);

    // 2. Load height zones (if configured)
    const heightZones = loadHeightZones(canvas, settings.heightDifferences);

    // 3. Load garden boundary (mandatory)
    const boundary = loadGardenBoundary(canvas, settings.outline);

    // 4. Assign layers to all wizard objects
    // The data.type field is already set on these objects, which getLayerForObject() uses
    if (bgImage) {
      assignLayerToObject(bgImage);
    }
    if (boundary) {
      assignLayerToObject(boundary);
    }
    heightZones.forEach(({ polygon, label }) => {
      assignLayerToObject(polygon);
      assignLayerToObject(label);
    });

    // 5. Enforce layer order on entire canvas
    // This replaces the manual sendObjectToBack/bringObjectForward calls
    enforceLayerOrder(canvas);

    // Re-enable auto-render
    canvas.renderOnAddRemove = true;
    canvas.renderAll();
  }, [settings, loadBackgroundImage, loadGardenBoundary, loadHeightZones, assignLayerToObject, enforceLayerOrder]);

  /**
   * Save canvas to localStorage
   */
  const saveCanvas = useCallback(() => {
    if (!fabricCanvasRef.current) return;

    try {
      const canvas = fabricCanvasRef.current;

      // Drop grid, wizard geometry and all editor scaffolding (ghosts, drawing
      // markers, vertex handles) — shared with the backend save path (#683).
      const canvasObjects = canvas.getObjects();
      const objectsToSave = canvasObjects.filter(isPersistableObject);

      // Create a minimal JSON with only the objects we want
      const json = {
        version: '6.0.0',
        objects: objectsToSave.map(obj => {
          // For PlantCircle objects, use their toObject() method which includes all plant metadata
          if (obj.objectType === 'plant') {
            const plantJson = obj.toObject();

            // Reset active state in the JSON (not the live object)
            if (plantJson.data?.isActive) {
              plantJson.data.isActive = false;
            }

            // Don't save overlap state - it will be recalculated on load
            delete plantJson.isOverlapping;
            delete plantJson.overlapState;

            return plantJson;
          }

          // For user-created Group objects, serialize with sub-object handling
          if (isUserGroup(obj)) {
            return serializeGroupObject(obj);
          }

          // For regular objects, use standard serialization
          // Include custom properties in serialization
          const objJson = obj.toJSON(['data', 'objectType', 'renderOptions']);

          // Manually add custom properties (Fabric.js v6 doesn't always serialize them correctly)
          if (obj.data) {
            objJson.data = obj.data;
          }
          if (obj.objectType) {
            objJson.objectType = obj.objectType;
          }
          if (obj.renderOptions) {
            objJson.renderOptions = obj.renderOptions;
          }

          // Reset active state in the JSON (not the live object)
          if (objJson.data?.isActive) {
            objJson.data.isActive = false;
          }

          // Strip Pattern fills before saving — patterns are regenerated on load
          // from data.material via textureManager. Saving Pattern objects causes
          // async image loading during enlivenObjects which leads to race conditions.
          if (objJson.fill && typeof objJson.fill === 'object' && objJson.fill.type === 'pattern') {
            const objType = objJson.data?.gardenObjectType || objJson.data?.objectType;
            const defaultStyle = objType ? getDefaultStyle(objType) : null;
            objJson.fill = defaultStyle?.fill || '#CCCCCC';
          }

          return objJson;
        })
      };

      // Save to localStorage
      localStorage.setItem('gartenplaner_canvas', JSON.stringify(json));
    } catch (error) {
      // Handle quota exceeded or other errors
      if (error.name === 'QuotaExceededError') {
        console.error('localStorage quota exceeded. Canvas not saved.');
        // TODO: Show user notification
      } else {
        console.error('Error saving canvas:', error);
      }
    }
  }, []);

  /**
   * Debounced save (1000ms after last change)
   */
  const debouncedSave = useCallback(() => {
    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      saveCanvas();
    }, 1000);
    // Mark garden as dirty for backend persistence
    if (onCanvasDirty) onCanvasDirty();
  }, [saveCanvas, onCanvasDirty]);

  /**
   * Load canvas from localStorage.
   *
   * Delegates the load sequence to `applyCanvasObjects` (single source of
   * truth; also used by the `externalCanvasData` effect for the backend path).
   */
  const loadCanvas = useCallback(async () => {
    if (!fabricCanvasRef.current) return;

    try {
      const saved = localStorage.getItem('gartenplaner_canvas');
      if (!saved) return;

      const json = JSON.parse(saved);
      await applyCanvasObjects(fabricCanvasRef.current, json, {
        source: 'localStorage',
        assignLayerToObject,
        enforceLayerOrder,
        // eslint-disable-next-line react-hooks/immutability -- applyTabObjectBehavior is declared just below; referenced only inside this useCallback body, which runs after mount when the binding is initialized
        applyTabObjectBehavior,
        activeTab,
        readOnly,
      });
    } catch (error) {
      console.error('Error loading canvas:', error);
      // Clear corrupted data
      localStorage.removeItem('gartenplaner_canvas');
      console.warn('Corrupted canvas data cleared. Starting with empty canvas.');
      // applyCanvasObjects already restores renderOnAddRemove on its error path,
      // but reset defensively in case JSON.parse threw before the helper ran.
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.renderOnAddRemove = true;
      }
    }
  }, [assignLayerToObject, enforceLayerOrder]); // eslint-disable-line react-hooks/exhaustive-deps -- applyTabObjectBehavior/activeTab/readOnly resolved at call time, not render time (loadCanvas is invoked once during the mount-only init effect)

  // Apply tab-specific object behavior (Phase 1.6 Integration)
  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- reads isObjectInteractionLockedRef.current (a stable ref, latest lock lookup) so the callback stays []-stable; lock re-application is driven by the effect below re-running on layerLocked/objectViewState, not by this callback's identity (#761)
  const applyTabObjectBehavior = useCallback((canvas, tab, isReadOnly = false) => {
    const objects = canvas.getObjects();

    objects.forEach(obj => {
      // Skip grid lines, snap guides, and wizard UI elements
      if (obj.data?.isGridLine || obj.data?.isSnapGuide) {
        return;
      }

      // Get object metadata
      const isPlantable = obj.data?.isPlantable;
      const isPlant = obj.objectType === 'plant'; // PlantCircle objects
      const isWizardObject = obj.data?.type === 'wizard-background' ||
                             obj.data?.type === 'garden-boundary' ||
                             obj.data?.type === 'height-zone' ||
                             obj.data?.type === 'height-zone-label';

      // A locked layer (or individually locked object) must never be raised to
      // selectable/evented by the tab logic — otherwise Fabric's rubber-band
      // selection (which collects over `selectable`) picks it up even though the
      // click path (which honours `evented`) blocks it. (#761)
      const locked = isObjectInteractionLockedRef.current(obj);

      if (tab === 'pflanzen') {
        // PFLANZEN TAB: Lock garden objects, plants stay selectable

        if (isPlant) {
          // Plants: full opacity on Pflanzen tab; selectable unless locked
          obj.set({
            selectable: !locked,
            evented: !locked,
            opacity: 1,
          });
        } else if (isWizardObject) {
          // Garden outline and wizard objects: 20% opacity, locked
          obj.set({
            selectable: false,
            evented: false,
            opacity: 0.2,
          });
        } else if (isPlantable) {
          // Planting zones (Beet, Rasen, etc.): 70% opacity, locked
          obj.set({
            selectable: false,
            evented: false,
            opacity: 0.7,
          });
        } else {
          // Structures (Haus, Zaun, Mauer, etc.): 40% opacity, locked
          obj.set({
            selectable: false,
            evented: false,
            opacity: 0.4,
          });
        }
      } else {
        // ZEICHNEN & GARTEN TABS: Garden objects selectable, plants locked/transparent

        if (isPlant) {
          // Plants: Locked and semi-transparent on Zeichnen/Garten tabs
          obj.set({
            selectable: false,
            evented: false,
            opacity: 0.3,
          });
        } else if (!isWizardObject) {
          // Restore full opacity for user objects; selectable unless locked
          obj.set({
            selectable: !locked,
            evented: !locked,
            opacity: obj.data?.originalOpacity !== undefined ? obj.data.originalOpacity : 1,
          });
        } else {
          // Wizard objects stay locked but at their configured opacity
          obj.set({
            selectable: false,
            evented: false,
            // Keep wizard object opacity (background image, outline, height zones have their own)
          });
        }
      }
    });

    if (isReadOnly) {
      objects.forEach(obj => {
        if (obj.data?.isGridLine || obj.data?.isSnapGuide) return;
        obj.set({ selectable: true, evented: true });
      });
    }

    canvas.requestRenderAll();
  }, []);

  useEffect(() => {
    if (!fabricCanvasRef.current) return;
    applyTabObjectBehavior(fabricCanvasRef.current, activeTab, readOnly);
    // Re-run on lock/visibility changes too: applyTabObjectBehavior is the single
    // authority for selectable/evented as a function of (tab × lock), so a lock
    // toggle must re-flow through it — even when the Ebenen panel is unmounted. (#761)
  }, [activeTab, applyTabObjectBehavior, readOnly, layerLocked, layerVisibility, objectViewState]);

  // Reload the canvas when an authoritative load replaces the backend snapshot.
  // Delegates to applyCanvasObjects (shared with loadCanvas's localStorage path).
  //
  // Keyed on externalCanvasNonce, not on the snapshot's object identity (#680):
  // since the save path keeps the snapshot in step with the server, its value
  // changes after every save — including the silent 30s auto-save. Reloading on
  // that would clear and re-enliven the whole canvas mid-edit and drop the
  // user's selection. The nonce is bumped only by a real load or reset
  // (useGardenLoader, "Neuer Garten"), which is exactly when a reload is wanted.
  useEffect(() => {
    if (!externalCanvasData || !fabricCanvasRef.current) return;

    (async () => {
      try {
        await applyCanvasObjects(fabricCanvasRef.current, externalCanvasData, {
          source: 'backend',
          assignLayerToObject,
          enforceLayerOrder,
          applyTabObjectBehavior,
          activeTab,
          readOnly,
        });
      } catch (error) {
        console.error('Error loading canvas from backend data:', error);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- externalCanvasData read at call time by design (see comment above); activeTab intentionally excluded: tab switching is handled by the dedicated applyTabObjectBehavior effect; including it here would re-run the full canvas load on every tab change, destroying wizard objects
  }, [externalCanvasNonce, assignLayerToObject, enforceLayerOrder, applyTabObjectBehavior, readOnly]);

  // Initialize Fabric.js canvas
  useEffect(() => {
    if (!canvasRef.current) return;

    // Prevent double initialization in React Strict Mode
    if (fabricCanvasRef.current) {
      return;
    }

    // Create Fabric canvas
    // Note: backgroundColor is intentionally omitted — CSS `background-color: #fff`
    // on the canvas element provides the white background so GridOverlay (which uses
    // the before:render event) can draw grid lines behind all objects.
    const canvas = new Canvas(canvasRef.current, {
      width: width,
      height: height,
      selection: false, // Disable selection box (enable only for select tool)
      preserveObjectStacking: true,
      uniformScaling: false, // Allow non-uniform scaling globally
      // Fabric v7 changed defaults; pin to expected behavior.
      // Right-Click → mouse:down event for context menu (useContextMenuActions).
      // Middle-Click is unhandled; suppress the v7-default event stream.
      fireRightClick: true,
      fireMiddleClick: false,
    });

    fabricCanvasRef.current = canvas;

    // E2E hook: expose Fabric canvas so Playwright can map canvas-space points to
    // screen coordinates via viewportTransform/getZoom (no DOM-level back-reference
    // exists in Fabric v6). Mirrors ScaleCalibrator's `window.__scaleCalibratorFabric`.
    if (typeof window !== 'undefined') {
      window.__gardenCanvas = canvas;
    }

    // Prevent iOS/Android Safari from intercepting drags for native browser scroll/zoom
    // before Fabric.js sees them. Without this, single-finger pan and pinch-zoom on touch
    // devices fail (#320).
    if (canvas.upperCanvasEl) canvas.upperCanvasEl.style.touchAction = 'none';
    if (canvas.lowerCanvasEl) canvas.lowerCanvasEl.style.touchAction = 'none';
    if (canvas.wrapperEl) canvas.wrapperEl.style.touchAction = 'none';

    // Disable uniform scaling globally
    canvas.uniformScaling = false;

    // Expose snap settings on canvas for custom controls (e.g., line endpoint handles)
    canvas.gridSize = gridSize;
    canvas.snapEnabled = snapEnabled;

    // Enable zoom and pan (returns cleanup function)
    // eslint-disable-next-line react-hooks/immutability -- setupZoomAndPan is a hoisted function declaration (defined below); this forward reference inside the effect body is valid
    const cleanupZoomAndPan = setupZoomAndPan(canvas);

    // Keep edge/segment measurement labels (polygon, curve) readable when zoomed out
    const detachLabelScaling = attachMeasurementLabelScaling(canvas);

    // Mark as ready BEFORE loading canvas (so fabricCanvasRef is set)
    setIsReady(true);
    setFabricCanvas(canvas);
    if (onCanvasReady) {
      onCanvasReady(canvas);
    }

    // Load wizard data first, then user-drawn objects (after canvas is ready).
    // Backend canvas data may already be in context before this canvas mounts —
    // since #484 the garden is loaded at app startup (GardenStartupLoader), so by
    // the time the editor opens externalCanvasData is usually already populated.
    // In that case the `externalCanvasData` effect below early-returns on mount
    // (canvas not ready yet) and never re-fires (value unchanged), so apply it
    // here. Otherwise fall back to the localStorage cache; a backend load that
    // completes *after* mount is still handled by the effect (value changes).
    // Store the timer id so cleanup can cancel it: under StrictMode the
    // mount effect runs twice, and an uncanceled first timeout would fire
    // against the second canvas (the ref is read at call time) — two
    // interleaved applyCanvasObjects runs duplicated every object (#622).
    const initialLoadTimeoutId = setTimeout(async () => {
      await loadWizardData();  // Load wizard objects first
      if (externalCanvasData) {
        await applyCanvasObjects(fabricCanvasRef.current, externalCanvasData, {
          source: 'backend',
          assignLayerToObject,
          enforceLayerOrder,
          applyTabObjectBehavior,
          activeTab,
          readOnly,
        });
      } else {
        await loadCanvas();    // Then load user-drawn objects from localStorage
      }
    }, 0);

    // Cleanup
    return () => {
      clearTimeout(initialLoadTimeoutId);

      // Save immediately before unmount
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveCanvas();
      }

      // Push unsaved changes to the backend too (#680). saveCanvas above only
      // writes localStorage, which the editor no longer reads when a backend
      // snapshot exists — so without this, edits from the last <30s (between
      // the change and the next auto-save tick) were effectively lost on the
      // way to the dashboard. Runs before dispose() below: the handler reads
      // the live canvas, and a disposed one reports zero objects.
      // Push unsaved changes to the backend too (#680). saveCanvas above only
      // writes localStorage, which the editor no longer reads when a backend
      // snapshot exists — so without this, edits from the last <30s (between
      // the change and the next auto-save tick) were effectively lost on the
      // way to the dashboard. Runs before dispose() below: the handler reads
      // the live canvas, and a disposed one reports zero objects.
      if (onUnmountFlushRef.current) {
        try {
          onUnmountFlushRef.current();
        } catch (error) {
          console.error('Error flushing unsaved changes on unmount:', error);
        }
      }

      // Clean up keyboard event listeners
      if (cleanupZoomAndPan) {
        cleanupZoomAndPan();
      }
      detachLabelScaling();
      // Clean up canvas
      if (fabricCanvasRef.current) {
        if (typeof window !== 'undefined' && window.__gardenCanvas === fabricCanvasRef.current) {
          delete window.__gardenCanvas;
        }
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
      setFabricCanvas(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Auto-save canvas on changes
  useEffect(() => {
    if (!isReady || !fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;

    // Listen for canvas changes
    canvas.on('object:modified', debouncedSave);
    canvas.on('object:added', debouncedSave);
    canvas.on('object:removed', debouncedSave);
    canvas.on('layer:reordered', debouncedSave);
    // Per-object hide/lock toggles (#760): the state itself lives in
    // settings.objectViewState, but for user content it is keyed by the object's
    // data.objectId — save canvas_objects so that id survives a reload.
    canvas.on('object:viewstate:changed', debouncedSave);

    return () => {
      canvas.off('object:modified', debouncedSave);
      canvas.off('object:added', debouncedSave);
      canvas.off('object:removed', debouncedSave);
      canvas.off('layer:reordered', debouncedSave);
      canvas.off('object:viewstate:changed', debouncedSave);
    };
  }, [isReady, debouncedSave]);

  // Layer system: Assign layers and enforce order on object:added
  useEffect(() => {
    if (!isReady || !fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;

    const handleObjectAdded = (e) => {
      const obj = e.target;

      // Skip grid lines, snap guides, transient vertex handles, and placement ghosts
      if (obj?.data?.isGridLine || obj?.data?.isSnapGuide || obj?.data?.isGhost || isVertexHandle(obj)) return;

      // Assign layer if not already set; pass canvas so new objects get
      // a zIndexWithinLayer that places them on top within their layer
      assignLayerToObject(obj, canvas);

      // Enforce layer order after adding
      enforceLayerOrder(canvas);
    };

    canvas.on('object:added', handleObjectAdded);

    return () => {
      canvas.off('object:added', handleObjectAdded);
    };
  }, [isReady, assignLayerToObject, enforceLayerOrder]);

  // Layer + per-object visibility sync: apply visibility changes from
  // LayerContext to canvas objects. This effect lives on the always-mounted
  // canvas (not the LayerPanel, which unmounts on a tab switch), so per-object
  // hide state (#760) is re-applied immediately after a reload — consistent
  // with how layer visibility already behaves — without needing the Ebenen tab
  // to be opened first.
  useEffect(() => {
    if (!isReady || !fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;
    const objects = canvas.getObjects();
    let needsRender = false;

    objects.forEach(obj => {
      // Skip grid lines and snap guides (always visible)
      if (obj.data?.isGridLine || obj.data?.isSnapGuide) return;

      // Get layer for this object
      const layerId = obj.data?.layer;
      if (!layerId) return;

      // Visible unless the layer is hidden OR the object is individually hidden
      const objectHidden = isObjectHidden(obj);
      const shouldBeVisible = layerVisibility[layerId] !== false && !objectHidden;
      // A locked object stays non-interactive even when (re)shown, so a
      // hide/show cycle can't quietly re-raise selectable on it. (#761)
      const locked = isLayerLocked(layerId) || isObjectLocked(obj);

      // Update visibility and selectability
      if (obj.visible !== shouldBeVisible) {
        obj.visible = shouldBeVisible;
        obj.selectable = shouldBeVisible && !locked;
        obj.evented = shouldBeVisible && !locked;
        needsRender = true;
      }
    });

    // Render if any visibility changed
    if (needsRender) {
      canvas.requestRenderAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isObjectHidden is recreated each render but reads objectViewState, which is the real trigger
  }, [isReady, layerVisibility, objectViewState]);

  // Load wizard data when settings change (e.g., after wizard completion)
  useEffect(() => {
    if (!isReady || !fabricCanvasRef.current) return;

    // Only load wizard data if we have an outline (mandatory wizard data)
    if (settings.outline?.vertices && settings.outline.vertices.length >= 3) {
      // Remove old wizard objects before reloading
      const canvas = fabricCanvasRef.current;
      const objectsToRemove = canvas.getObjects().filter(obj =>
        obj.data?.type === 'wizard-background' ||
        obj.data?.type === 'garden-boundary' ||
        obj.data?.type === 'height-zone' ||
        obj.data?.type === 'height-zone-label'
      );

      objectsToRemove.forEach(obj => canvas.remove(obj));

      // Load wizard data
      loadWizardData();
    }
  }, [isReady, settings.outline, settings.backgroundImage, settings.heightDifferences, loadWizardData]);

  // Auto-zoom to fit garden boundary after wizard completion (runs once)
  useEffect(() => {
    if (!isReady || !fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;
    const autoZoomDone = localStorage.getItem('gartenplaner_auto_zoom_done');
    const wizardComplete = localStorage.getItem('gartenplaner_wizard_complete');

    // Only auto-zoom if:
    // 1. Wizard has been completed
    // 2. Auto-zoom hasn't been done yet
    // 3. We have garden outline vertices
    if (wizardComplete && !autoZoomDone && settings.outline?.vertices && settings.outline.vertices.length >= 3) {
      // Add a small delay to ensure wizard data is fully loaded
      const timeoutId = setTimeout(() => {
        zoomToFitGarden(canvas, settings.outline.vertices, 0.1, 300)
          .then(() => {
            // Mark auto-zoom as done so it doesn't run again
            localStorage.setItem('gartenplaner_auto_zoom_done', 'true');
          })
          .catch((error) => {
            console.error('Auto-zoom to fit failed:', error);
          });
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isReady, settings.outline]);

  // Read-only-Ansicht (Mobile): beim Öffnen immer auf den Garten einpassen.
  // Der Auto-Zoom oben läuft wegen des globalen localStorage-Flags nur ein
  // einziges Mal überhaupt — auf dem Smartphone zeigte der Editor sonst bei
  // Zoom 1:1 nur den linken Ausschnitt der Zeichenfläche. Re-fit bei
  // width/height-Änderung (Geräterotation); der 100-ms-Versatz lässt das
  // setDimensions-Effect unten zuerst wirken.
  useEffect(() => {
    if (!isReady || !readOnly || !fabricCanvasRef.current) return;
    const vertices = settings.outline?.vertices;
    if (!vertices || vertices.length < 3) return;

    const canvas = fabricCanvasRef.current;
    const timeoutId = setTimeout(() => {
      zoomToFitGarden(canvas, vertices, 0.1, 0).catch((error) => {
        console.error('Read-only zoom-to-fit failed:', error);
      });
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [isReady, readOnly, width, height, settings.outline]);

  // Handle selection changes
  useEffect(() => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;

    const handleSelectionCreated = (e) => {
      // Get the active object from canvas - this is the ActiveSelection or single object
      const activeObject = canvas.getActiveObject();

      // Update appearance for selected objects (active state = green)
      const selectedObjects = e.selected || [];
      previouslySelectedObjectsRef.current = selectedObjects; // Track for later deselection

      // Vertex handles are transient — don't update appearance or notify parent
      if (selectedObjects.length === 1 && isVertexHandle(selectedObjects[0])) return;

      selectedObjects.forEach((obj) => {
        if (obj.data?.type === 'rectangle') {
          updateRectangleAppearance(obj, true);
        } else if (obj.data?.type === 'ellipse') {
          updateEllipseAppearance(obj, true);
        } else if (obj.data?.type === 'line') {
          updateLineAppearance(obj, true);
        } else if (obj.data?.type === 'polygon') {
          updatePolygonAppearance(obj, true);
        } else if (obj.data?.type === 'bezier') {
          updateBezierAppearance(obj, true);
        } else if (obj.objectType === 'plant' && obj.selectOuterRing) {
          obj.selectOuterRing();
        }
      });

      if (selectedObjects.length > 0) {
        canvas.requestRenderAll();
      }

      if (onSelectionChange) {
        // For multi-selection, pass the ActiveSelection; for single, pass the first object
        const isMulti = activeObject?.type === 'activeselection';
        onSelectionChange(isMulti ? activeObject : selectedObjects[0]);
      }
    };

    const handleSelectionUpdated = (e) => {
      const selectedObjects = e.selected || [];
      const deselectedObjects = e.deselected || [];

      // Vertex handles are transient — skip selection updates for them
      if (selectedObjects.length === 1 && isVertexHandle(selectedObjects[0])) return;

      // Deselected: Restore original config colors (skip handles)
      deselectedObjects.forEach((obj) => {
        if (obj.data?.type === 'rectangle') {
          updateRectangleAppearance(obj, false);
        } else if (obj.data?.type === 'ellipse') {
          updateEllipseAppearance(obj, false);
        } else if (obj.data?.type === 'line') {
          updateLineAppearance(obj, false);
        } else if (obj.data?.type === 'polygon') {
          updatePolygonAppearance(obj, false);
        } else if (obj.data?.type === 'bezier') {
          updateBezierAppearance(obj, false);
        } else if (obj.objectType === 'plant' && obj.deselectOuterRing) {
          obj.deselectOuterRing();
        }
      });

      // Selected: Use active green color
      selectedObjects.forEach((obj) => {
        if (obj.data?.type === 'rectangle') {
          updateRectangleAppearance(obj, true);
        } else if (obj.data?.type === 'ellipse') {
          updateEllipseAppearance(obj, true);
        } else if (obj.data?.type === 'line') {
          updateLineAppearance(obj, true);
        } else if (obj.data?.type === 'polygon') {
          updatePolygonAppearance(obj, true);
        } else if (obj.data?.type === 'bezier') {
          updateBezierAppearance(obj, true);
        } else if (obj.objectType === 'plant' && obj.selectOuterRing) {
          obj.selectOuterRing();
        }
      });

      previouslySelectedObjectsRef.current = selectedObjects; // Update tracked selection
      canvas.requestRenderAll();

      if (onSelectionChange) {
        // Get the active object from canvas - this is the ActiveSelection or single object
        const activeObject = canvas.getActiveObject();
        const isMulti = activeObject?.type === 'activeselection';
        onSelectionChange(isMulti ? activeObject : selectedObjects[0]);
      }
    };

    const handleSelectionCleared = () => {
      // Restore original colors for previously selected objects
      previouslySelectedObjectsRef.current.forEach((obj) => {
        if (obj.data?.type === 'rectangle') {
          updateRectangleAppearance(obj, false);
        } else if (obj.data?.type === 'ellipse') {
          updateEllipseAppearance(obj, false);
        } else if (obj.data?.type === 'line') {
          updateLineAppearance(obj, false);
        } else if (obj.data?.type === 'polygon') {
          updatePolygonAppearance(obj, false);
        } else if (obj.data?.type === 'bezier') {
          updateBezierAppearance(obj, false);
        } else if (obj.objectType === 'plant' && obj.deselectOuterRing) {
          obj.deselectOuterRing();
        }
      });

      previouslySelectedObjectsRef.current = []; // Clear tracking
      canvas.requestRenderAll();

      if (onSelectionChange) {
        onSelectionChange(null);
      }
    };

    const handleMouseDownForTransform = (e) => {
      // Capture transform start state for undo/redo (skip vertex handles — they use ModifyObjectCommand)
      if (e.target && activeTool === 'select' && !isVertexHandle(e.target)) {
        // Lines: raw x1..y2 go stale on moves (fabric only updates left/top);
        // the endpoint controls + scaling handler later set x1..y2 and would
        // re-derive left/top from the stale values → opposite endpoint jumps.
        // Normalize BEFORE capturing the transform snapshot (#611).
        normalizeLineRawCoords(e.target);

        transformStartStateRef.current = {
          left: e.target.left,
          top: e.target.top,
          scaleX: e.target.scaleX,
          scaleY: e.target.scaleY,
          angle: e.target.angle,
          // Openings (gates + windows): snapshot link state — dragging can
          // attach/detach/slide (#611, #690)
          ...(isOpening(e.target) && {
            gateLink: e.target.data?.link ? { ...e.target.data.link } : null,
          }),
        };
      }
    };

    const handleObjectModified = (e) => {
      const modified = e.target;

      // Vertex handles are transient — skip transform command and panel update
      if (isVertexHandle(modified)) return;

      // Create undo/redo command for transform operations
      if (transformStartStateRef.current && activeTool === 'select') {
        const { gateLink, ...oldTransform } = transformStartStateRef.current;

        const newTransform = {
          left: modified.left,
          top: modified.top,
          scaleX: modified.scaleX,
          scaleY: modified.scaleY,
          angle: modified.angle,
        };

        // Openings: include attach/detach/slide in the command so undo restores the link (#611, #690)
        let linkState = null;
        if (isOpening(modified) && 'gateLink' in transformStartStateRef.current) {
          const linkAfter = modified.data?.link ? { ...modified.data.link } : null;
          if (JSON.stringify(gateLink) !== JSON.stringify(linkAfter)) {
            linkState = { before: gateLink, after: linkAfter };
          }
        }

        // Only create command if something actually changed
        const hasChanged =
          linkState !== null ||
          Object.keys(newTransform).some(
            key => oldTransform[key] !== newTransform[key]
          );

        if (hasChanged) {
          const command = new TransformObjectCommand(
            modified,
            oldTransform,
            newTransform,
            linkState
          );
          commandStack.execute(command);
          // Note: TransformObjectCommand.execute() will call updateOverlapStates() if needed
        }

        transformStartStateRef.current = null;
      }

      // Note: Overlap detection is now handled by the Commands themselves
      // (CreateObjectCommand, TransformObjectCommand, ModifyObjectCommand)
      // This prevents duplicate overlap checks and improves performance

      // Show overlap hint tooltip when a plant's state changes after move
      if (modified?.objectType === 'plant' && modified.overlapState && modified.overlapState !== 'none') {
        const canvasEl = canvas.getElement();
        const rect = canvasEl.getBoundingClientRect();
        const vpt = canvas.viewportTransform;
        const sx = rect.left + modified.left * vpt[0] + vpt[4];
        const sy = rect.top + modified.top * vpt[3] + vpt[5];
        setOverlapHint({ state: modified.overlapState, x: sx, y: sy });
      } else if (modified?.objectType === 'plant') {
        setOverlapHint(null);
      }

      // Notify parent that object was moved/scaled/rotated
      if (onSelectionChange && modified) {
        // Force properties panel to update by triggering selection change
        onSelectionChange(modified);
      }
    };

    canvas.on('mouse:down', handleMouseDownForTransform);
    canvas.on('selection:created', handleSelectionCreated);
    canvas.on('selection:updated', handleSelectionUpdated);
    canvas.on('selection:cleared', handleSelectionCleared);
    canvas.on('object:modified', handleObjectModified);

    return () => {
      canvas.off('mouse:down', handleMouseDownForTransform);
      canvas.off('selection:created', handleSelectionCreated);
      canvas.off('selection:updated', handleSelectionUpdated);
      canvas.off('selection:cleared', handleSelectionCleared);
      canvas.off('object:modified', handleObjectModified);
    };
  }, [onSelectionChange, activeTool, commandStack]);

  // Plant hover tooltip
  useEffect(() => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;

    const handleMouseOver = (e) => {
      if (!e.target) return;
      if (e.target.objectType !== 'plant') return;
      if (placementMode) return;
      if (activeTool !== 'select') return;

      // Show outer ring immediately on hover
      if (e.target.showOuterRing) {
        e.target.showOuterRing();
        canvas.requestRenderAll();
      }

      hoverTimeoutRef.current = setTimeout(() => {
        const canvasEl = canvas.getElement();
        const rect = canvasEl.getBoundingClientRect();
        const vpt = canvas.viewportTransform;

        // PlantCircle center (left/top) → screen coordinates
        const screenX = rect.left + e.target.left * vpt[0] + vpt[4];
        const screenY = rect.top + e.target.top * vpt[3] + vpt[5];

        setHoveredPlant({
          plantName: e.target.plantName,
          screenX,
          screenY,
        });
      }, 200);
    };

    const handleMouseOut = (e) => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      setHoveredPlant(null);

      // Hide outer ring when mouse leaves plant
      if (e?.target?.objectType === 'plant' && e.target.hideOuterRing) {
        e.target.hideOuterRing();
        canvas.requestRenderAll();
      }
    };

    const handleObjectMovingForTooltip = () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      setHoveredPlant(null);
    };

    canvas.on('mouse:over', handleMouseOver);
    canvas.on('mouse:out', handleMouseOut);
    canvas.on('object:moving', handleObjectMovingForTooltip);

    return () => {
      canvas.off('mouse:over', handleMouseOver);
      canvas.off('mouse:out', handleMouseOut);
      canvas.off('object:moving', handleObjectMovingForTooltip);
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, [activeTool, placementMode]);

  // Handle keyboard events for tool modifiers
  useEffect(() => {
    window.addEventListener('keydown', tool.handleKeyDown);
    window.addEventListener('keyup', tool.handleKeyUp);

    return () => {
      window.removeEventListener('keydown', tool.handleKeyDown);
      window.removeEventListener('keyup', tool.handleKeyUp);
    };
  }, [tool.handleKeyDown, tool.handleKeyUp]);

  // Enable/disable selection box and target-find based on active tool, and manage
  // cursor for select/pan modes. Drawing tools manage their own cursor downstream.
  useEffect(() => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;

    if (activeTool === 'select') {
      canvas.selection = true;
      canvas.skipTargetFind = false; // Enable hit-detection for object interaction
      canvas.defaultCursor = 'default';
      canvas.setCursor('default');
    } else if (activeTool === 'pan') {
      canvas.selection = false;
      canvas.skipTargetFind = true;
      canvas.defaultCursor = 'grab';
      canvas.setCursor('grab');
    } else {
      canvas.selection = false;
      canvas.skipTargetFind = true; // Skip hit-detection during drawing (expensive with 100+ objects)
    }
    canvas.requestRenderAll();
  }, [activeTool]);

  // Handle Delete/Backspace key to delete selected object
  useEffect(() => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;

    const handleKeyDown = (e) => {
      // Check if Delete or Backspace key is pressed
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // Don't delete if user is typing in an input field
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
          return;
        }

        const activeObject = canvas.getActiveObject();
        if (activeObject && onObjectDelete) {
          // Store object reference and show confirmation dialog
          setObjectToDelete(activeObject);
          setShowDeleteDialog(true);
          e.preventDefault(); // Prevent browser back navigation on Backspace
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onObjectDelete]);

  // Nudge the selected object(s) with the arrow keys (#769).
  // Step size follows the snap setting: no snap → 1 cm (1 px = 1 cm), snap on →
  // one grid width; Shift = large step (10×). Ctrl/Cmd overrides snap for a
  // single free step, consistent with the drawing tools' snap override.
  useEffect(() => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;

    const ARROW_DELTAS = {
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
    };

    const handleKeyDown = (e) => {
      const delta = ARROW_DELTAS[e.key];
      if (!delta) return;

      // In 3D the 2D canvas stays mounted but hidden and keeps its selection
      // (#688). Arrow keys pan the 3D camera there (#770) — don't also nudge the
      // hidden object out of view.
      if (viewMode === '3d') return;

      // Don't move objects while typing in a field
      if (
        e.target.tagName === 'INPUT' ||
        e.target.tagName === 'TEXTAREA' ||
        e.target.isContentEditable
      ) {
        return;
      }

      // Not while editing polygon vertices — the handles have their own logic
      if (editingPolygonRef.current) return;

      const activeObject = canvas.getActiveObject();
      if (!activeObject || isVertexHandle(activeObject)) return;

      // Locked objects (or objects on a locked layer) don't move (#761)
      const targets =
        activeObject.type === 'activeselection' && activeObject.getObjects
          ? activeObject.getObjects()
          : [activeObject];
      if (targets.some((obj) => isObjectInteractionLockedRef.current(obj))) return;

      e.preventDefault();

      const useSnap = snapEnabled && !(e.ctrlKey || e.metaKey);
      const base = useSnap ? gridSize : 1; // 1 px = 1 cm
      const step = e.shiftKey ? base * 10 : base;

      const oldTransform = {
        left: activeObject.left,
        top: activeObject.top,
        scaleX: activeObject.scaleX,
        scaleY: activeObject.scaleY,
        angle: activeObject.angle,
      };
      const newTransform = {
        ...oldTransform,
        left: activeObject.left + delta[0] * step,
        top: activeObject.top + delta[1] * step,
      };

      // TransformObjectCommand handles setCoords(), gate-sync, overlap and
      // undo/redo; firing object:modified triggers the auto-save path.
      const command = new TransformObjectCommand(activeObject, oldTransform, newTransform);
      commandStack.execute(command);
      canvas.fire('object:modified', { target: activeObject });
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [commandStack, snapEnabled, gridSize, editingPolygonRef, viewMode]);

  // Ctrl+C / Ctrl+V / Ctrl+D clipboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore when typing in input fields
      if (
        e.target.tagName === 'INPUT' ||
        e.target.tagName === 'TEXTAREA' ||
        e.target.isContentEditable
      ) {
        return;
      }

      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 'c':
            contextMenuActions.copy();
            break;
          case 'v':
            contextMenuActions.paste();
            break;
          case 'd':
            e.preventDefault(); // Prevent browser bookmark shortcut
            contextMenuActions.duplicate();
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [contextMenuActions]);

  // Ctrl+G → Gruppieren, Ctrl+Shift+G → Gruppierung lösen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        e.target.tagName === 'INPUT' ||
        e.target.tagName === 'TEXTAREA' ||
        e.target.isContentEditable
      ) {
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'g') {
        e.preventDefault();
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;

        if (e.shiftKey) {
          // Ctrl+Shift+G → Gruppierung lösen
          const activeObj = canvas.getActiveObject();
          if (isUserGroup(activeObj)) {
            // Exit edit mode first if active
            if (editingGroupRef.current === activeObj) {
              exitEditMode(canvas, activeObj);
              editingGroupRef.current = null;
            }
            commandStack.execute(new UngroupObjectsCommand(canvas, activeObj));
          }
        } else {
          // Ctrl+G → Gruppieren
          const activeObj = canvas.getActiveObject();
          if (activeObj?.type === 'activeselection' && activeObj.getObjects().length >= 2) {
            const objects = [...activeObj.getObjects()];
            canvas.discardActiveObject();
            commandStack.execute(new GroupObjectsCommand(canvas, objects));
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [commandStack]);

  // Group edit mode: double-click to enter, Escape / click-outside to exit
  useEffect(() => {
    if (!isReady || !fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;

    const handleDblClick = (e) => {
      const target = e.target;

      // Double-click on a polygon → vertex edit mode (#179)
      // User-drawn polygons have objectType === 'polygon' (set by createGardenObject).
      // Wizard polygons (garden-boundary, height-zone) are locked and excluded via data.locked.
      if (target?.type === 'polygon' && target?.objectType === 'polygon' && !target?.data?.locked) {
        enterVertexEditMode(target);
        return;
      }

      // Double-click on a bezier path or a centerline weg (#720) → anchor vertex
      // edit mode. Both are fabric.Path objects edited by the same hook; the hook
      // regenerates the geometry per data.shapeType/verlauf.
      if (
        target?.type === 'path' &&
        (target?.data?.shapeType === 'bezier' || target?.data?.shapeType === 'centerline') &&
        !target?.data?.locked
      ) {
        enterBezierVertexEditMode(target);
        return;
      }

      // Double-click on a user group → enter edit mode
      if (isUserGroup(target) && !editingGroupRef.current) {
        enterEditMode(canvas, target);
        editingGroupRef.current = target;
        return;
      }

      // Double-click on a nested group while already in edit mode → go deeper
      if (editingGroupRef.current && isUserGroup(e.subTargets?.[0])) {
        exitEditMode(canvas, editingGroupRef.current);
        enterEditMode(canvas, e.subTargets[0]);
        editingGroupRef.current = e.subTargets[0];
      }
    };

    // Click anywhere that is NOT the editing group/polygon/path or one of its handles → exit edit mode
    const handleMouseDown = (e) => {
      // Polygon vertex edit mode exit
      if (editingPolygonRef.current) {
        const target = e.target;
        // Stay in edit mode when clicking the polygon itself or a vertex handle
        if (target === editingPolygonRef.current) return;
        if (isVertexHandle(target)) return;
        exitVertexEditMode();
        return;
      }

      // Bezier vertex edit mode exit
      if (editingPathRef.current) {
        const target = e.target;
        // Stay in edit mode when clicking the path itself or a vertex handle
        if (target === editingPathRef.current) return;
        if (isVertexHandle(target)) return;
        exitBezierVertexEditMode();
        return;
      }

      if (!editingGroupRef.current) return;
      const target = e.target;
      const group = editingGroupRef.current;
      // Stay in edit mode when clicking on the group itself or a direct sub-object
      if (target === group) return;
      if (target && group.getObjects?.().includes(target)) return;
      exitEditMode(canvas, group);
      editingGroupRef.current = null;
    };

    // Escape key → exit edit mode
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (editingPolygonRef.current) {
          exitVertexEditMode();
        } else if (editingPathRef.current) {
          exitBezierVertexEditMode();
        } else if (editingGroupRef.current) {
          exitEditMode(canvas, editingGroupRef.current);
          editingGroupRef.current = null;
        }
      }
    };

    canvas.on('mouse:dblclick', handleDblClick);
    canvas.on('mouse:down', handleMouseDown);
    window.addEventListener('keydown', handleEscape);

    return () => {
      canvas.off('mouse:dblclick', handleDblClick);
      canvas.off('mouse:down', handleMouseDown);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isReady, editingPolygonRef, enterVertexEditMode, exitVertexEditMode, editingPathRef, enterBezierVertexEditMode, exitBezierVertexEditMode]);

  // Handle F key for zoom-to-fit garden
  useEffect(() => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;

    const handleKeyDown = (e) => {
      // Check if F key is pressed (not Ctrl+F which is browser search)
      if (e.key === 'f' && !e.ctrlKey && !e.metaKey) {
        // Don't trigger if user is typing in an input field
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
          return;
        }

        // Only zoom if we have garden outline vertices
        if (settings.outline?.vertices && settings.outline.vertices.length >= 3) {
          e.preventDefault(); // Prevent any default browser behavior
          zoomToFitGarden(canvas, settings.outline.vertices, 0.1, 300)
            .catch((error) => {
              console.error('Manual zoom-to-fit failed:', error);
            });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [settings.outline]);

  // Right-click context menu — use Fabric.js 'contextmenu' canvas event
  useEffect(() => {
    if (!isReady || !fabricCanvasRef.current || !containerRef.current) return;

    const canvas = fabricCanvasRef.current;
    const container = containerRef.current;

    const handleContextMenu = (fabricEvent) => {
      // Prevent browser native context menu
      fabricEvent.e.preventDefault();

      const target = fabricEvent.target;
      const isSelectableTarget = target && !target.data?.isGridLine && !target.data?.isSnapGuide;

      // Select the right-clicked object if not already active.
      // Preserve an active multi-selection (#471): if the target is already part
      // of the ActiveSelection, keep it intact so the context menu still offers
      // "Gruppieren" + "Kopieren" for the whole selection instead of collapsing
      // to a single-object selection.
      const active = canvas.getActiveObject();
      const isActiveSelection = active?.type === 'activeselection';
      const targetInActiveSelection =
        isActiveSelection && active.getObjects?.().includes(target);

      if (isSelectableTarget && !targetInActiveSelection && active !== target) {
        canvas.setActiveObject(target);
        canvas.requestRenderAll();
      }

      const containerRect = container.getBoundingClientRect();
      setContextMenu({
        visible: true,
        x: fabricEvent.e.clientX - containerRect.left,
        y: fabricEvent.e.clientY - containerRect.top,
        target: isSelectableTarget ? target : null,
      });
    };

    canvas.on('contextmenu', handleContextMenu);

    return () => {
      canvas.off('contextmenu', handleContextMenu);
    };
  }, [isReady]);

  // Snap-to-grid for moving and scaling objects (reactive to grid settings)
  useEffect(() => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;
    let isModifierPressed = false;

    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        isModifierPressed = true;
      }
    };

    const handleKeyUp = (e) => {
      if (!e.ctrlKey && !e.metaKey) {
        isModifierPressed = false;
      }
    };

    const handleObjectMoving = (e) => {
      const obj = e.target;

      // Vertex handles snap themselves in useVertexEditing.js moving handler
      if (obj.data?.type === 'vertex-handle') {
        return;
      }

      // Snap position
      // eslint-disable-next-line react-hooks/immutability -- snapValue is a hoisted function declaration (defined below); valid forward reference inside this handler
      const snappedLeft = snapValue(
        obj.left,
        gridSize,
        snapEnabled,
        isModifierPressed
      );
      const snappedTop = snapValue(
        obj.top,
        gridSize,
        snapEnabled,
        isModifierPressed
      );

      obj.set({
        left: snappedLeft,
        top: snappedTop,
      });

      // Show visual guides if snapped
      // showSnapGuides/clearSnapGuides are hoisted function declarations (defined below)
      if (snapEnabled && !isModifierPressed) {
        // eslint-disable-next-line react-hooks/immutability -- valid forward reference to hoisted function declaration
        showSnapGuides(canvas, snappedLeft, snappedTop);
      } else {
        // eslint-disable-next-line react-hooks/immutability -- valid forward reference to hoisted function declaration
        clearSnapGuides(canvas);
      }
    };

    const handleObjectScaling = (e) => {
      const obj = e.transform.target;

      // Special handling for lines - keep opposite point fixed during scaling
      if (obj.type === 'line') {
        // Store the original coordinates at the start of the transform
        if (!e.transform.original.x1) {
          e.transform.original.x1 = obj.x1;
          e.transform.original.y1 = obj.y1;
          e.transform.original.x2 = obj.x2;
          e.transform.original.y2 = obj.y2;
        }

        const origX1 = e.transform.original.x1;
        const origY1 = e.transform.original.y1;
        const origX2 = e.transform.original.x2;
        const origY2 = e.transform.original.y2;

        // Determine which corner is being dragged
        const corner = e.transform.corner;

        // Keep the opposite point fixed and only move the dragged point
        if (corner === 'tl' || corner === 'bl' || corner === 'p1') {
          // Dragging start point (x1, y1) - keep end point (x2, y2) fixed
          obj.set({
            x2: origX2,
            y2: origY2,
          });
        } else if (corner === 'tr' || corner === 'br' || corner === 'p2') {
          // Dragging end point (x2, y2) - keep start point (x1, y1) fixed
          obj.set({
            x1: origX1,
            y1: origY1,
          });
        }

        // For lines, snap the coordinates to grid if enabled
        if (snapEnabled && !isModifierPressed) {
          obj.set({
            x1: snapValue(obj.x1, gridSize, snapEnabled, isModifierPressed),
            y1: snapValue(obj.y1, gridSize, snapEnabled, isModifierPressed),
            x2: snapValue(obj.x2, gridSize, snapEnabled, isModifierPressed),
            y2: snapValue(obj.y2, gridSize, snapEnabled, isModifierPressed),
          });
        }

        // Mindestlänge erzwingen, damit eine Linie nicht auf ~0 kollabiert und
        // in 3D als entartetes Band verschwindet (#699).
        const draggingP1 = corner === 'tl' || corner === 'bl' || corner === 'p1';
        const clampedPoint = clampLineEndpoint({
          x1: obj.x1,
          y1: obj.y1,
          x2: obj.x2,
          y2: obj.y2,
          draggingP1,
        });
        if (clampedPoint) {
          obj.set(
            draggingP1
              ? { x1: clampedPoint.x, y1: clampedPoint.y }
              : { x2: clampedPoint.x, y2: clampedPoint.y }
          );
        }

        obj.setCoords();
        return;
      }

      // Snap (falls aktiv) und Mindestausdehnung im gemeinsamen Skalier-Pfad —
      // gilt für alle über Scale-Handles skalierbaren Typen (Rect/Ellipse/
      // Polygon/Path), damit kein Objekt auf ~0 gezogen werden kann und in 3D
      // verschwindet (#699).
      const isEllipse = obj.type === 'ellipse';
      const resolved = resolveScalingTransform({
        // Ellipsen tragen die Maße in rx/ry, Zentrums-Ursprung; alle übrigen
        // in width/height mit Top-Left-Ursprung.
        baseW: isEllipse ? obj.rx * 2 : obj.width,
        baseH: isEllipse ? obj.ry * 2 : obj.height,
        scaleX: obj.scaleX,
        scaleY: obj.scaleY,
        corner: e.transform.corner,
        hasCenterOrigin: obj.originX === 'center' && obj.originY === 'center',
        snap: { enabled: snapEnabled && !isModifierPressed, gridSize },
        original: e.transform.original,
      });

      if (resolved) {
        obj.set(resolved);
      }
    };

    const handleMouseUp = () => {
      clearSnapGuides(canvas);
    };

    // Register event listeners
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    canvas.on('object:moving', handleObjectMoving);
    canvas.on('object:scaling', handleObjectScaling);
    canvas.on('mouse:up', handleMouseUp);

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      canvas.off('object:moving', handleObjectMoving);
      canvas.off('object:scaling', handleObjectScaling);
      canvas.off('mouse:up', handleMouseUp);
    };
  }, [snapEnabled, gridSize]);

  // -------------------------------------------------------------------------
  // Gate ↔ structure linking (#611)
  // - Linked gates follow their parent structure live (move/rotate/scale)
  // - Dragging a gate snaps it onto / slides it along structure edges;
  //   dragging away beyond GATE_DETACH_THRESHOLD breaks the link,
  //   dragging a standalone gate near a structure attaches it
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!fabricCanvas) return;
    const canvas = fabricCanvas;

    // All gate work is deferred to a microtask so it runs AFTER every other
    // synchronous object:moving/scaling handler (grid snap, line endpoint
    // logic) but before the next paint — the derived gate position always
    // wins, independent of handler registration order.
    const pendingGates = new Set(); // gates being dragged → re-snap to edges
    const pendingParents = new Set(); // rigid parent transforms → sync via stored t
    const reprojectParents = new Set(); // parent geometry changed → re-derive t
    const detachParents = new Set(); // drag ended → cut links on too-short edges
    let flushScheduled = false;
    let disposed = false;

    const resnapGate = (gate) => {
      const center = gate.getCenterPoint();
      const hasLink = !!gate.data?.link;
      // Linked openings get a wider corridor before the link breaks
      const threshold = hasLink ? GATE_DETACH_THRESHOLD : GATE_ATTACH_THRESHOLD;
      // Only offer parents this opening kind may attach to — a window (#690)
      // never docks onto a fence or wall, only buildings/greenhouses.
      const allowedParentTypes = parentsForOpening(getOpeningTypeId(gate));
      const snap = findNearestEdgePoint(
        { x: center.x, y: center.y },
        findLinkableParents(canvas, allowedParentTypes),
        threshold
      );

      if (snap) {
        const gateWidthPx = (gate.width || 0) * (gate.scaleX || 1);
        const t = clampGateT(snap.t, snap.edgeLength, gateWidthPx);
        const pos = pointOnEdgeAt(snap.edge, t);
        gate.data = {
          ...gate.data,
          link: {
            parentId: ensureObjectId(snap.parent),
            edgeIndex: snap.edgeIndex,
            t,
          },
        };
        gate.set({ angle: snap.angleDeg });
        setObjectCenterAt(gate, pos);
        applyInheritedHeight(gate, snap.parent);
        gate.setCoords();
      } else if (hasLink) {
        // Dragged beyond the detach threshold (or parent deleted) → standalone
        const { link: _removed, ...rest } = gate.data;
        gate.data = rest;
      }
    };

    const flush = () => {
      flushScheduled = false;
      if (disposed) return;
      pendingGates.forEach(resnapGate);
      pendingParents.forEach((parent) => syncLinkedGates(canvas, parent));
      reprojectParents.forEach((parent) => reprojectLinkedGates(canvas, parent));
      detachParents.forEach((parent) => detachGatesFromShortEdges(canvas, parent));
      pendingGates.clear();
      pendingParents.clear();
      reprojectParents.clear();
      detachParents.clear();
      canvas.requestRenderAll();
    };

    const schedule = (set, target) => {
      set.add(target);
      if (!flushScheduled) {
        flushScheduled = true;
        queueMicrotask(flush);
      }
    };

    // Transform events don't reliably carry e.target (the existing scaling
    // handler reads e.transform.target for the same reason)
    const eventTarget = (e) => e.target || e.transform?.target || null;

    const handleObjectMovingForGates = (e) => {
      const target = eventTarget(e);
      if (!target) return;
      if (isOpening(target)) {
        schedule(pendingGates, target);
      } else if (isLinkableParent(target)) {
        schedule(pendingParents, target);
      }
    };

    // Rigid transform (rotation) → stored t stays valid
    const handleParentRotatingForGates = (e) => {
      const target = eventTarget(e);
      if (target && isLinkableParent(target)) {
        schedule(pendingParents, target);
      }
    };

    // Geometry change (line endpoint drag, rect scaling) → keep the gate at
    // its world position by re-deriving t instead of sliding proportionally
    const handleParentScalingForGates = (e) => {
      const target = eventTarget(e);
      if (target && isLinkableParent(target)) {
        schedule(reprojectParents, target);
      }
    };

    // Drag end: route by transform action — geometry changes (endpoint drag,
    // scaling) re-project t, rigid moves/rotations sync via stored t
    const handleParentModifiedForGates = (e) => {
      const target = eventTarget(e);
      if (!target || !isLinkableParent(target)) return;
      const action = e.action || e.transform?.action;
      if (action && action !== 'drag' && action !== 'rotate') {
        schedule(reprojectParents, target);
      } else {
        schedule(pendingParents, target);
      }
      // Structure shrunk below the gate width → gate can't fit, cut the link
      schedule(detachParents, target);
    };

    // The line endpoint controls (LineTool) bypass fabric's canvas-level
    // transform events — they only emit object-level 'scaling' via
    // line.fire('scaling'). Attach per-line listeners for live reprojection.
    const lineScalingHandlers = new Map();
    const attachLineScaling = (obj) => {
      if (obj?.type === 'line' && isLinkableParent(obj) && !lineScalingHandlers.has(obj)) {
        const handler = () => schedule(reprojectParents, obj);
        obj.on('scaling', handler);
        lineScalingHandlers.set(obj, handler);
      }
    };
    canvas.getObjects().forEach(attachLineScaling);
    const handleObjectAddedForGates = (e) => attachLineScaling(e.target);

    canvas.on('object:moving', handleObjectMovingForGates);
    canvas.on('object:rotating', handleParentRotatingForGates);
    canvas.on('object:scaling', handleParentScalingForGates);
    canvas.on('object:modified', handleParentModifiedForGates);
    canvas.on('object:added', handleObjectAddedForGates);

    return () => {
      disposed = true;
      canvas.off('object:moving', handleObjectMovingForGates);
      canvas.off('object:rotating', handleParentRotatingForGates);
      canvas.off('object:scaling', handleParentScalingForGates);
      canvas.off('object:modified', handleParentModifiedForGates);
      canvas.off('object:added', handleObjectAddedForGates);
      lineScalingHandlers.forEach((handler, obj) => obj.off('scaling', handler));
      lineScalingHandlers.clear();
    };
  }, [fabricCanvas]);

  // Rectangle drawing tool
  useEffect(() => {
    if (!fabricCanvasRef.current || activeTool !== 'rectangle') {
      return;
    }

    const canvas = fabricCanvasRef.current;

    // Disable selection while drawing
    const handleMouseDown = (e) => {
      if (e.target) return; // Clicked on an existing object

      const pointer = canvas.getScenePoint(e.e);
      tool.startDrawing({ x: pointer.x, y: pointer.y });

      // Create temporary rectangle
      const bounds = calculateRectangleBounds(
        { x: pointer.x, y: pointer.y },
        { x: pointer.x, y: pointer.y },
        tool.shiftPressed,
        tool.altPressed,
        tool.ctrlPressed,
        gridSize,
        snapEnabled,
        canvas // Pass canvas for endpoint snapping
      );

      const rect = createRectangle(bounds, true, selectedObjectType);
      tool.drawingObjectRef.current = rect;
      canvas.add(rect);
      canvas.selection = false;
    };

    const handleMouseMove = (e) => {
      if (!tool.isDrawing || !tool.drawingObjectRef.current) return;

      const pointer = canvas.getScenePoint(e.e);

      // Update mouse position for tooltip
      setMousePosition({ x: e.e.clientX, y: e.e.clientY });

      // Calculate new bounds with modifiers
      const bounds = calculateRectangleBounds(
        tool.startPoint,
        { x: pointer.x, y: pointer.y },
        tool.shiftPressed,
        tool.altPressed,
        tool.ctrlPressed,
        gridSize,
        snapEnabled,
        canvas // Pass canvas for endpoint snapping
      );

      // Update dimensions for tooltip with snapped values
      tool.dimensions.width = bounds.width;
      tool.dimensions.height = bounds.height;

      // Update rectangle
      tool.drawingObjectRef.current.set(bounds);
      canvas.requestRenderAll();

      // Show snap guides if snapping
      if (snapEnabled && !tool.ctrlPressed) {
        showSnapGuides(canvas, bounds.left, bounds.top);
      } else {
        clearSnapGuides(canvas);
      }
    };

    const handleMouseUp = () => {
      if (!tool.isDrawing || !tool.drawingObjectRef.current) return;

      const rect = tool.drawingObjectRef.current;

      // Remove tiny rectangles (accidental clicks)
      if (rect.width < 5 || rect.height < 5) {
        canvas.remove(rect);
      } else {
        // Finalize rectangle - make it selectable
        rect.set({
          selectable: true,
          evented: true,
        });

        // Remove from canvas (was added on mouse:down)
        canvas.remove(rect);

        // Create command and execute (adds back to canvas with command tracking)
        const command = new CreateObjectCommand(canvas, rect);
        commandStack.execute(command);

        // Select the object in Fabric.js (triggers selection:created event)
        canvas.setActiveObject(rect);
        canvas.requestRenderAll();
      }

      // Clear snap guides
      clearSnapGuides(canvas);

      // Complete drawing
      tool.completeDrawing();
      setMousePosition(null);

      // Switch to select tool (this will trigger the useEffect that enables canvas.selection)
      if (onToolChange) {
        onToolChange('select');
      }

      canvas.requestRenderAll();
    };

    canvas.on('mouse:down', handleMouseDown);
    canvas.on('mouse:move', handleMouseMove);
    canvas.on('mouse:up', handleMouseUp);

    // Change cursor
    canvas.defaultCursor = 'crosshair';
    canvas.setCursor('crosshair');

    return () => {
      canvas.off('mouse:down', handleMouseDown);
      canvas.off('mouse:move', handleMouseMove);
      canvas.off('mouse:up', handleMouseUp);
      canvas.defaultCursor = 'default';
      canvas.setCursor('default');
      // Don't re-enable selection here - let the activeTool useEffect handle it
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTool,
    tool,
    gridSize,
    snapEnabled,
    onToolChange,
  ]);

  // Circle/Ellipse drawing tool
  useEffect(() => {
    if (!fabricCanvasRef.current || activeTool !== 'circle') {
      return;
    }

    const canvas = fabricCanvasRef.current;

    // Disable selection while drawing
    const handleMouseDown = (e) => {
      if (e.target) return; // Clicked on an existing object

      // Disable the selection box (the blue rectangle that appears when dragging)
      canvas.selection = false;

      const pointer = canvas.getScenePoint(e.e);
      tool.startDrawing({ x: pointer.x, y: pointer.y });

      // Create temporary ellipse (starting from center)
      const radii = calculateEllipseRadii(
        { x: pointer.x, y: pointer.y },
        { x: pointer.x, y: pointer.y },
        tool.shiftPressed,
        tool.ctrlPressed,
        gridSize,
        snapEnabled,
        canvas // Pass canvas for endpoint snapping
      );

      const ellipse = createEllipse(radii, true, selectedObjectType);
      tool.drawingObjectRef.current = ellipse;
      canvas.add(ellipse);
      canvas.discardActiveObject(); // Ensure no object is selected
      canvas.requestRenderAll();
    };

    const handleMouseMove = (e) => {
      if (!tool.isDrawing || !tool.drawingObjectRef.current) return;

      const pointer = canvas.getScenePoint(e.e);

      // Update mouse position for tooltip
      setMousePosition({ x: e.e.clientX, y: e.e.clientY });

      // Calculate new radii with modifiers
      const radii = calculateEllipseRadii(
        tool.startPoint,
        { x: pointer.x, y: pointer.y },
        tool.shiftPressed,
        tool.ctrlPressed,
        gridSize,
        snapEnabled,
        canvas // Pass canvas for endpoint snapping
      );

      // Update dimensions for tooltip (use radiusX/radiusY instead of width/height)
      tool.dimensions.radiusX = radii.radiusX;
      tool.dimensions.radiusY = radii.radiusY;

      // Update ellipse
      tool.drawingObjectRef.current.set({
        left: radii.centerX,
        top: radii.centerY,
        rx: radii.radiusX,
        ry: radii.radiusY,
      });
      canvas.requestRenderAll();

      // Show snap guides if snapping (for center point)
      if (snapEnabled && !tool.ctrlPressed) {
        showSnapGuides(canvas, radii.centerX, radii.centerY);
      } else {
        clearSnapGuides(canvas);
      }
    };

    const handleMouseUp = () => {
      if (!tool.isDrawing || !tool.drawingObjectRef.current) return;

      const ellipse = tool.drawingObjectRef.current;

      // Remove tiny ellipses (accidental clicks)
      if (ellipse.rx < 5 || ellipse.ry < 5) {
        canvas.remove(ellipse);
      } else {
        // Finalize ellipse - make it selectable and re-enable caching
        ellipse.set({
          selectable: true,
          evented: true,
          hasControls: true,
          hasBorders: true,
          objectCaching: true,  // Re-enable caching after drawing (was disabled for live preview)
          noScaleCache: true,   // Skip cache regeneration on viewport zoom
        });

        // Remove from canvas (was added on mouse:down)
        canvas.remove(ellipse);

        // Create command and execute (adds back to canvas with command tracking)
        const command = new CreateObjectCommand(canvas, ellipse);
        commandStack.execute(command);

        // Select the object in Fabric.js (triggers selection:created event)
        canvas.setActiveObject(ellipse);
        canvas.requestRenderAll();
      }

      // Clear snap guides
      clearSnapGuides(canvas);

      // Complete drawing
      tool.completeDrawing();
      setMousePosition(null);

      // Switch to select tool (this will trigger the useEffect that enables canvas.selection)
      if (onToolChange) {
        onToolChange('select');
      }

      canvas.requestRenderAll();
    };

    canvas.on('mouse:down', handleMouseDown);
    canvas.on('mouse:move', handleMouseMove);
    canvas.on('mouse:up', handleMouseUp);

    // Change cursor
    canvas.defaultCursor = 'crosshair';
    canvas.setCursor('crosshair');

    return () => {
      canvas.off('mouse:down', handleMouseDown);
      canvas.off('mouse:move', handleMouseMove);
      canvas.off('mouse:up', handleMouseUp);
      canvas.defaultCursor = 'default';
      canvas.setCursor('default');
      // Don't re-enable selection here - let the activeTool useEffect handle it
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTool,
    tool,
    gridSize,
    snapEnabled,
    onToolChange,
  ]);

  // Line drawing tool
  useEffect(() => {
    if (!fabricCanvasRef.current || activeTool !== 'line') {
      return;
    }

    const canvas = fabricCanvasRef.current;

    // Disable selection while drawing
    const handleMouseDown = (e) => {
      if (e.target) return; // Clicked on an existing object

      const pointer = canvas.getScenePoint(e.e);
      tool.startDrawing({ x: pointer.x, y: pointer.y });

      // Create temporary line
      const coords = calculateLineCoordinates(
        { x: pointer.x, y: pointer.y },
        { x: pointer.x, y: pointer.y },
        tool.ctrlPressed,
        gridSize,
        snapEnabled,
        canvas // Pass canvas for endpoint snapping
      );

      const line = createLine(coords.x1, coords.y1, coords.x2, coords.y2, selectedObjectType);
      line.set({
        selectable: false,
        evented: false,
        opacity: 0.5, // Preview opacity
      });

      tool.drawingObjectRef.current = line;
      canvas.add(line);
      canvas.selection = false;
    };

    const handleMouseMove = (e) => {
      if (!tool.isDrawing || !tool.drawingObjectRef.current) return;

      const pointer = canvas.getScenePoint(e.e);

      // Update mouse position for tooltip
      setMousePosition({ x: e.e.clientX, y: e.e.clientY });

      // Calculate new coordinates with snap
      const coords = calculateLineCoordinates(
        tool.startPoint,
        { x: pointer.x, y: pointer.y },
        tool.ctrlPressed,
        gridSize,
        snapEnabled,
        canvas // Pass canvas for endpoint snapping
      );

      // Calculate length and angle for tooltip
      const { length, angle } = calculateLineProperties(
        { x: coords.x1, y: coords.y1 },
        { x: coords.x2, y: coords.y2 }
      );

      // Update dimensions for tooltip
      tool.dimensions.length = pixelsToCm(length);
      tool.dimensions.angle = Math.round(angle);

      // Update line coordinates
      tool.drawingObjectRef.current.set({
        x1: coords.x1,
        y1: coords.y1,
        x2: coords.x2,
        y2: coords.y2,
      });
      tool.drawingObjectRef.current.setCoords();
      canvas.requestRenderAll();

      // Show snap guides if snapping
      if (snapEnabled && !tool.ctrlPressed) {
        // Show guides at both endpoints
        showSnapGuides(canvas, coords.x1, coords.y1);
        showSnapGuides(canvas, coords.x2, coords.y2);
      } else {
        clearSnapGuides(canvas);
      }
    };

    const handleMouseUp = () => {
      if (!tool.isDrawing || !tool.drawingObjectRef.current) return;

      const line = tool.drawingObjectRef.current;

      // Calculate final length
      const { length } = calculateLineProperties(
        { x: line.x1, y: line.y1 },
        { x: line.x2, y: line.y2 }
      );

      // Remove very short lines (accidental clicks)
      if (length < 5) {
        canvas.remove(line);
      } else {
        // Finalize line - make it selectable
        line.set({
          selectable: true,
          evented: true,
          opacity: 1,
        });

        // Note: line.data is already set by LineTool.createLine() via createGardenObject()
        // DO NOT overwrite it here - it contains garden object metadata (objectTypeId, gardenObjectType, etc.)

        // Remove from canvas (was added on mouse:down)
        canvas.remove(line);

        // Create command and execute (adds back to canvas with command tracking)
        const command = new CreateObjectCommand(canvas, line);
        commandStack.execute(command);

        // Select the object in Fabric.js (triggers selection:created event)
        canvas.setActiveObject(line);
        canvas.requestRenderAll();
      }

      // Clear snap guides
      clearSnapGuides(canvas);

      // Complete drawing
      tool.completeDrawing();
      setMousePosition(null);

      // Switch to select tool
      if (onToolChange) {
        onToolChange('select');
      }

      canvas.requestRenderAll();
    };

    // Handle Escape key to cancel drawing
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && tool.isDrawing) {
        // Cancel drawing
        if (tool.drawingObjectRef.current) {
          canvas.remove(tool.drawingObjectRef.current);
        }
        tool.completeDrawing();
        setMousePosition(null);
        clearSnapGuides(canvas);
        canvas.requestRenderAll();
      }
    };

    canvas.on('mouse:down', handleMouseDown);
    canvas.on('mouse:move', handleMouseMove);
    canvas.on('mouse:up', handleMouseUp);
    window.addEventListener('keydown', handleKeyDown);

    // Change cursor
    canvas.defaultCursor = 'crosshair';
    canvas.setCursor('crosshair');

    return () => {
      canvas.off('mouse:down', handleMouseDown);
      canvas.off('mouse:move', handleMouseMove);
      canvas.off('mouse:up', handleMouseUp);
      window.removeEventListener('keydown', handleKeyDown);
      canvas.defaultCursor = 'default';
      canvas.setCursor('default');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTool,
    tool,
    gridSize,
    snapEnabled,
    onToolChange,
  ]);

  // Update canvas size when width/height props change
  useEffect(() => {
    if (fabricCanvasRef.current) {
      fabricCanvasRef.current.setDimensions({
        width: width,
        height: height
      });
      fabricCanvasRef.current.renderAll();
    }
  }, [width, height]);

  // Update snap settings on canvas for custom controls (e.g., line endpoint handles)
  useEffect(() => {
    if (fabricCanvasRef.current) {
      fabricCanvasRef.current.gridSize = gridSize;
      fabricCanvasRef.current.snapEnabled = snapEnabled;
    }
  }, [gridSize, snapEnabled]);

  /**
   * Snap value to grid
   */
  // Hoisted function declaration so the snap/zoom helpers can be referenced by the
  // event-handler effects defined earlier in the component (react-hooks/immutability
  // flags `const` arrows used before their declaration; function declarations hoist).
  function snapValue(value, gridSize, snapEnabled, modifierPressed) {
    if (!snapEnabled || modifierPressed) {
      return value;
    }
    return Math.round(value / gridSize) * gridSize;
  };

  /**
   * Show visual snap guides
   */
  function showSnapGuides(canvas, x, y) {
    const canvasWidth = canvas.getWidth();
    const canvasHeight = canvas.getHeight();

    // Reuse existing guides if they exist
    if (snapGuidesRef.current.length === 2) {
      const [verticalGuide, horizontalGuide] = snapGuidesRef.current;

      // Update positions instead of recreating
      verticalGuide.set({
        x1: x,
        y1: 0,
        x2: x,
        y2: canvasHeight,
      });

      horizontalGuide.set({
        x1: 0,
        y1: y,
        x2: canvasWidth,
        y2: y,
      });

      verticalGuide.setCoords();
      horizontalGuide.setCoords();
      return; // Don't call renderAll here - caller will do it
    }

    // Create guides only if they don't exist
    const verticalGuide = new Line([x, 0, x, canvasHeight], {
      originX: 'left',
      originY: 'top',
      stroke: '#10B981', // Emerald-500
      strokeWidth: 1,
      strokeDashArray: [5, 5],
      selectable: false,
      evented: false,
      data: { isSnapGuide: true },
      opacity: 0.6,
    });

    const horizontalGuide = new Line([0, y, canvasWidth, y], {
      originX: 'left',
      originY: 'top',
      stroke: '#10B981', // Emerald-500
      strokeWidth: 1,
      strokeDashArray: [5, 5],
      selectable: false,
      evented: false,
      data: { isSnapGuide: true },
      opacity: 0.6,
    });

    canvas.add(verticalGuide, horizontalGuide);
    snapGuidesRef.current = [verticalGuide, horizontalGuide];
    canvas.requestRenderAll();
  };

  /**
   * Clear snap guides
   */
  function clearSnapGuides(canvas) {
    snapGuidesRef.current.forEach((guide) => canvas.remove(guide));
    snapGuidesRef.current = [];
  };

  /**
   * Setup zoom and pan functionality
   */
  function setupZoomAndPan(canvas) {
    let isPanning = false;
    let lastPosX = 0;
    let lastPosY = 0;
    let spacePressed = false;
    let prevSelection = canvas.selection;
    let prevSkipTargetFind = canvas.skipTargetFind;
    let activeTouchCount = 0;

    // Extract pointer coords from PointerEvent or TouchEvent.
    // Why: TouchEvent has no top-level clientX/Y — they live on touches[0]/changedTouches[0].
    // Reading evt.clientX directly on a TouchEvent yields undefined → NaN viewport delta → blank canvas.
    const getEventCoords = (evt) => {
      if (typeof TouchEvent !== 'undefined' && evt instanceof TouchEvent) {
        const t = evt.touches?.[0] ?? evt.changedTouches?.[0];
        if (t) return { x: t.clientX, y: t.clientY };
      }
      return { x: evt.clientX, y: evt.clientY };
    };

    // Track spacebar state for pan mode
    const handleKeyDown = (e) => {
      // Ignore keyboard shortcuts when typing in input fields or textareas
      if (
        e.target.tagName === 'INPUT' ||
        e.target.tagName === 'TEXTAREA' ||
        e.target.isContentEditable
      ) {
        return;
      }

      if (e.code === 'Space' && !spacePressed) {
        spacePressed = true;
        canvas.defaultCursor = 'grab';
        canvas.setCursor('grab');
        e.preventDefault();
      }
      // Reset zoom with '0' key
      if (e.key === '0' && !e.ctrlKey && !e.metaKey) {
        canvas.setZoom(1);
        canvas.viewportTransform = [1, 0, 0, 1, 0, 0];
        canvas.requestRenderAll();

        e.preventDefault();
      }
      // Select All with Ctrl+A / Cmd+A (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault(); // Prevent browser's "select all text" behavior

        // Get all selectable objects (exclude grid lines and non-selectable objects)
        const allObjects = canvas.getObjects().filter((obj) => {
          // Exclude grid lines and snap guides
          if (obj.data?.isGridLine || obj.data?.isSnapGuide) return false;
          // Exclude non-selectable objects
          if (obj.selectable === false) return false;
          return true;
        });

        // Only create selection if there are objects to select
        if (allObjects.length > 0) {
          const selection = new ActiveSelection(allObjects, { canvas });
          canvas.setActiveObject(selection);
          canvas.requestRenderAll();
        }
      }
    };

    const handleKeyUp = (e) => {
      if (e.code === 'Space') {
        spacePressed = false;
        canvas.defaultCursor = 'default';
        if (!isPanning) {
          canvas.setCursor('default');
        }
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    // Mouse wheel zoom
    canvas.on('mouse:wheel', (opt) => {
      const delta = opt.e.deltaY;
      let zoom = canvas.getZoom();

      // Zoom in/out with ctrl/cmd + scroll
      zoom *= 0.999 ** delta;

      // Limit zoom range: 10% to 500%
      if (zoom > 5) zoom = 5;
      if (zoom < 0.1) zoom = 0.1;

      canvas.zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, zoom);

      opt.e.preventDefault();
      opt.e.stopPropagation();
    });

    const startPan = (evt) => {
      isPanning = true;
      prevSelection = canvas.selection;
      prevSkipTargetFind = canvas.skipTargetFind;
      canvas.selection = false;
      canvas.skipTargetFind = true; // Skip hit-detection while panning
      const { x, y } = getEventCoords(evt);
      lastPosX = x;
      lastPosY = y;
    };

    // Pan with middle mouse button, space + drag, or single-finger touch on empty canvas
    canvas.on('mouse:down', (opt) => {
      const evt = opt.e;
      const isTouch = evt.pointerType === 'touch' || (typeof TouchEvent !== 'undefined' && evt instanceof TouchEvent);

      // Active pan tool (e.g. mobile/tablet "Bewegen") — pan unconditionally,
      // including over objects. Must run before middle-click/space branches.
      if (activeToolRef.current === 'pan') {
        startPan(evt);
        canvas.setCursor('grabbing');
        return;
      }

      // Middle mouse button or space key held
      if (evt.button === 1 || (evt.button === 0 && spacePressed)) {
        startPan(evt);
        canvas.setCursor('grabbing');
        return;
      }

      // Single-finger touch on empty canvas → pan, but only with the select tool
      // and never while placing a plant. During placement the tool stays 'select',
      // so without this guard the finger would pan instead of placing (#748).
      // While a drawing tool is active the finger must draw the object (its own
      // mouse:down/move/up handlers), not pan — otherwise both start at once and
      // the user has to hold one finger and drag with a second (#747).
      if (
        isTouch &&
        !opt.target &&
        activeTouchCount <= 1 &&
        activeToolRef.current === 'select' &&
        !placementModeRef.current
      ) {
        startPan(evt);
      }
    });

    canvas.on('mouse:move', (opt) => {
      if (isPanning) {
        const { x, y } = getEventCoords(opt.e);
        if (Number.isNaN(x) || Number.isNaN(y)) return;
        const vpt = canvas.viewportTransform;

        vpt[4] += x - lastPosX;
        vpt[5] += y - lastPosY;

        canvas.requestRenderAll();
        lastPosX = x;
        lastPosY = y;
      }
    });

    canvas.on('mouse:up', () => {
      if (isPanning) {
        isPanning = false;
        canvas.selection = prevSelection;
        canvas.skipTargetFind = prevSkipTargetFind;
        const restCursor = activeToolRef.current === 'pan'
          ? 'grab'
          : (spacePressed ? 'grab' : 'default');
        canvas.setCursor(restCursor);
      }
    });

    // Track active touches so a 2nd-finger gesture can cancel an in-progress pan
    const handleTouchStart = (e) => {
      activeTouchCount = e.touches?.length ?? 0;
      // 2-finger gesture started while panning → cancel pan, let pinch-zoom take over
      if (activeTouchCount >= 2 && isPanning) {
        isPanning = false;
        canvas.selection = prevSelection;
        canvas.skipTargetFind = prevSkipTargetFind;
      }
    };
    const handleTouchEnd = (e) => {
      activeTouchCount = e.touches?.length ?? 0;
    };
    const upperCanvas = canvas.upperCanvasEl;
    if (upperCanvas) {
      upperCanvas.addEventListener('touchstart', handleTouchStart, { passive: true });
      upperCanvas.addEventListener('touchend', handleTouchEnd, { passive: true });
      upperCanvas.addEventListener('touchcancel', handleTouchEnd, { passive: true });
    }

    // Two-finger pinch-to-zoom on touch devices (#746). Fabric dropped its
    // `touch:gesture` event after v5, so pinch is wired onto native touchmove.
    const detachPinchZoom = attachPinchZoom(canvas);

    // Return cleanup function
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      detachPinchZoom();
      if (upperCanvas) {
        upperCanvas.removeEventListener('touchstart', handleTouchStart);
        upperCanvas.removeEventListener('touchend', handleTouchEnd);
        upperCanvas.removeEventListener('touchcancel', handleTouchEnd);
      }
    };
  };

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        minHeight: '100%',
        display: 'flex',
        alignItems: 'flex-start', // Changed from 'center' to 'flex-start'
        justifyContent: 'center',
        backgroundColor: '#FFFFFF',
        position: 'relative',
        paddingTop: '40px', // Space from top
        paddingBottom: '40px', // Reduced since controls now use fixed positioning
      }}
    >
      {/* Rotatable canvas wrapper — apply CSS transform for north-view mode */}
      <div
        data-testid="canvas-rotate-wrapper"
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          transform: northViewActive && settings.northAngle != null
            ? `rotate(${-settings.northAngle}deg)`
            : 'none',
          transformOrigin: 'center',
          transition: 'transform 0.3s ease',
          pointerEvents: northViewActive ? 'none' : 'auto',
        }}
      >
      <canvas
        ref={canvasRef}
        style={placementMode ? {
          boxShadow: '0 0 0 2px #10B981', // Emerald-500 border glow
        } : undefined}
      />

      {/* Grid overlay — draws grid lines via before:render hook, behind all objects */}
      {isReady && fabricCanvas && (
        <GridOverlay
          canvas={fabricCanvas}
          gridEnabled={gridEnabled}
          gridSize={gridSize}
          gridColor={gridColor}
          gridOpacity={gridOpacity}
        />
      )}

      {/* Grid controls */}
      <GridControls />

      {/* View-Panels Blüte/Sonne & Schatten leben seit #633 in MainLayoutContent
          (Desktop: Sidebar-Slot, Tablet/Mobile: floating) */}

      {/* Dimension tooltip for rectangle drawing */}
      <DimensionTooltip
        position={mousePosition}
        dimensions={tool.dimensions}
        type="rectangle"
        isSquare={tool.shiftPressed}
        visible={tool.isDrawing && activeTool === 'rectangle'}
        pixelsPerCm={1}
      />

      {/* Dimension tooltip for circle/ellipse drawing */}
      <DimensionTooltip
        position={mousePosition}
        dimensions={tool.dimensions}
        type="ellipse"
        isSquare={tool.shiftPressed}
        visible={tool.isDrawing && activeTool === 'circle'}
        pixelsPerCm={1}
      />

      {/* Dimension tooltip for line drawing */}
      <DimensionTooltip
        position={mousePosition}
        dimensions={tool.dimensions}
        type="line"
        visible={tool.isDrawing && activeTool === 'line'}
        pixelsPerCm={1}
      />

      {/* Plant hover tooltip */}
      <PlantHoverTooltip
        plantName={hoveredPlant?.plantName}
        position={hoveredPlant ? { x: hoveredPlant.screenX, y: hoveredPlant.screenY } : null}
        visible={!!hoveredPlant}
      />

      {/* Overlap state hint (brief fade-out overlay) */}
      <OverlapHintTooltip
        state={overlapHint?.state}
        position={overlapHint ? { x: overlapHint.x, y: overlapHint.y } : null}
        visible={!!overlapHint}
      />

      {/* Polygon drawing tool */}
      {activeTool === 'polygon' && fabricCanvas && (
        <PolygonDrawingTool
          canvas={fabricCanvas}
          gridSize={gridSize}
          snapEnabled={snapEnabled}
          selectedObjectType={selectedObjectType}
          onComplete={(_polygonObject) => {
            // Auto-switch to select tool after polygon creation
            onToolChange?.('select');
          }}
          onCancel={() => {
            // Switch back to select tool on cancel
            onToolChange?.('select');
          }}
        />
      )}

      {/* Bezier curve drawing tool */}
      {activeTool === 'curve' && fabricCanvas && (
        <BezierDrawingTool
          canvas={fabricCanvas}
          gridSize={gridSize}
          snapEnabled={snapEnabled}
          selectedObjectType={selectedObjectType}
          onComplete={() => {
            // Auto-switch to select tool after curve creation
            onToolChange?.('select');
          }}
          onCancel={() => {
            // Switch back to select tool on cancel
            onToolChange?.('select');
          }}
        />
      )}

      {/* Centerline drawing tool (weg, #720) — offene Mittellinie + Breite */}
      {activeTool === 'centerline' && fabricCanvas && (
        <CenterlineDrawingTool
          canvas={fabricCanvas}
          gridSize={gridSize}
          snapEnabled={snapEnabled}
          selectedObjectType={selectedObjectType}
          onComplete={() => {
            // Auto-switch to select tool after weg creation
            onToolChange?.('select');
          }}
          onCancel={() => {
            onToolChange?.('select');
          }}
        />
      )}

      {/* Opening placement tool (point tool, #611 gate + #690 window) */}
      {activeTool === 'point' && fabricCanvas && (
        <GatePlacementTool
          canvas={fabricCanvas}
          objectTypeId={selectedObjectType}
          onComplete={() => {
            // Auto-switch to select tool after gate placement
            onToolChange?.('select');
          }}
          onCancel={() => {
            onToolChange?.('select');
          }}
        />
      )}

      {/* Plant placement manager (click-to-place) */}
      {fabricCanvas && (
        <PlantPlacementManager
          canvas={fabricCanvas}
          isActive={activeTab === 'pflanzen'}
        />
      )}

      {/* Plant drag handler (drag-and-drop from Favorites Panel).
          containerRef is read inside PlantDragHandler's effects, not here. */}
      {fabricCanvas && (
        <PlantDragHandler
          canvas={fabricCanvas}
          containerRef={containerRef}
          isActive={activeTab === 'pflanzen'}
        />
      )}

      {/* Plant placement mode overlay */}
      {placementMode && (
        <div
          style={{
            position: 'absolute',
            top: '16px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(16, 185, 129, 0.95)', // Emerald-500
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 20,
            pointerEvents: 'none',
          }}
        >
          Klicken Sie in Ihren Garten, um die Pflanze zu platzieren (ESC zum Abbrechen)
        </div>
      )}
      </div>{/* end canvas-rotate-wrapper */}

      {/* North-view banner — shown outside the rotatable wrapper */}
      {northViewActive && (
        <button
          type="button"
          onClick={() => setNorthViewActive(false)}
          data-testid="north-view-banner"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: 'rgba(16, 185, 129, 0.9)',
            color: 'white',
            textAlign: 'center',
            padding: '8px 16px',
            border: 'none',
            width: '100%',
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
            zIndex: 20,
          }}
        >
          Norden-Ansicht aktiv — klicken zum Bearbeiten
        </button>
      )}

      {/* Compass widget — interactive, replaces static placeholder */}
      <CompassWidget
        northAngle={settings.northAngle}
        onNorthAngleChange={(angle) => updateSettings({ northAngle: angle })}
        northViewActive={northViewActive}
        onNorthViewToggle={() => setNorthViewActive((v) => !v)}
      />

      {/* Grundgefälle-Indikator (#624) — read-only, unter dem Kompass */}
      <SlopeIndicator
        baseSlope={settings.heightDifferences?.baseSlope ?? null}
        northAngle={settings.northAngle}
      />

      {/* Zoom controls — auf Mobile ausgeblendet: dort gibt es Pinch-Zoom,
          und die Buttons kollidierten mit dem MobileEditorBanner */}
      {!isMobile && <div style={{
        position: 'fixed', // Changed from 'absolute' to 'fixed' for viewport positioning
        bottom: '16px',
        right: overlayRight,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        backgroundColor: 'white',
        padding: '8px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        zIndex: 10,
      }}>
        <button
          onClick={() => {
            if (fabricCanvasRef.current) {
              const canvas = fabricCanvasRef.current;
              let zoom = canvas.getZoom() * 1.1;
              // Limit to 500%
              if (zoom > 5) zoom = 5;
              canvas.setZoom(zoom);
              canvas.requestRenderAll();
            }
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#F3F4F6';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
          style={{
            width: '32px',
            height: '32px',
            border: '1px solid #E5E7EB',
            borderRadius: '4px',
            backgroundColor: 'white',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            transition: 'all 0.2s',
          }}
          aria-label="Zoom in"
        >
          +
        </button>
        <button
          onClick={() => {
            if (fabricCanvasRef.current) {
              const canvas = fabricCanvasRef.current;
              let zoom = canvas.getZoom() * 0.9;
              // Limit to 10%
              if (zoom < 0.1) zoom = 0.1;
              canvas.setZoom(zoom);
              canvas.requestRenderAll();
            }
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#F3F4F6';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
          style={{
            width: '32px',
            height: '32px',
            border: '1px solid #E5E7EB',
            borderRadius: '4px',
            backgroundColor: 'white',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            transition: 'all 0.2s',
          }}
          aria-label="Zoom out"
        >
          −
        </button>
        <button
          onClick={() => {
            if (fabricCanvasRef.current) {
              const canvas = fabricCanvasRef.current;
              canvas.setZoom(1);
              canvas.viewportTransform = [1, 0, 0, 1, 0, 0];
              canvas.requestRenderAll();
            }
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#F3F4F6';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
          style={{
            width: '32px',
            height: '32px',
            border: '1px solid #E5E7EB',
            borderRadius: '4px',
            backgroundColor: 'white',
            cursor: 'pointer',
            fontSize: '10px',
            transition: 'all 0.2s',
          }}
          aria-label="Reset zoom"
        >
          1:1
        </button>
        <button
          onClick={() => {
            if (fabricCanvasRef.current && settings.outline?.vertices && settings.outline.vertices.length >= 3) {
              const canvas = fabricCanvasRef.current;
              zoomToFitGarden(canvas, settings.outline.vertices, 0.1, 300)
                .catch((error) => {
                  console.error('Manual zoom-to-fit failed:', error);
                });
            }
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#F3F4F6';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
          style={{
            width: '32px',
            height: '32px',
            border: '1px solid #E5E7EB',
            borderRadius: '4px',
            backgroundColor: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s',
            opacity: settings.outline?.vertices && settings.outline.vertices.length >= 3 ? 1 : 0.4,
            pointerEvents: settings.outline?.vertices && settings.outline.vertices.length >= 3 ? 'auto' : 'none',
          }}
          title="Garten einpassen (F)"
          aria-label="Zoom to fit garden"
          disabled={!settings.outline?.vertices || settings.outline.vertices.length < 3}
        >
          <Maximize2 size={16} />
        </button>
      </div>}

      {/* Status indicator */}
      {!isReady && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: '14px',
          color: '#6B7280',
        }}>
          Loading canvas...
        </div>
      )}

      {/* Canvas Context Menu */}
      {(() => {
        const target = contextMenu.target;
        const active = fabricCanvas?.getActiveObject();
        const isLocked = target?.data?.locked === true;
        const isMultiSelection = active?.type === 'activeselection';
        const isGroup = active?.type === 'group';
        const hasTarget = !!target && !target.data?.isGridLine && !target.data?.isSnapGuide;
        const hasSelection = !!active && !active.data?.isGridLine;

        const items = [];

        if (hasTarget || hasSelection) {
          items.push({
            label: 'Kopieren',
            shortcut: 'Strg+C',
            disabled: false,
            onClick: () => contextMenuActions.copy(),
          });
        }

        if (contextMenuActions.hasClipboard()) {
          items.push({
            label: 'Einfügen',
            shortcut: 'Strg+V',
            onClick: () => contextMenuActions.paste({ x: contextMenu.x, y: contextMenu.y }),
          });
        }

        if (hasTarget || hasSelection) {
          items.push({
            label: 'Duplizieren',
            shortcut: 'Strg+D',
            disabled: isLocked,
            onClick: () => contextMenuActions.duplicate(),
          });
          items.push({ separator: true });
          items.push({
            label: 'In den Vordergrund',
            disabled: isLocked,
            onClick: () => contextMenuActions.bringToFront(),
          });
          items.push({
            label: 'In den Hintergrund',
            disabled: isLocked,
            onClick: () => contextMenuActions.sendToBack(),
          });
          items.push({ separator: true });

          if (isGroup) {
            items.push({
              label: 'Gruppierung lösen',
              disabled: isLocked,
              onClick: () => contextMenuActions.ungroup(),
            });
          } else if (isMultiSelection) {
            items.push({
              label: 'Gruppieren',
              disabled: isLocked,
              onClick: () => contextMenuActions.group(),
            });
          }

          items.push({ separator: true });
          items.push({
            label: 'Löschen',
            shortcut: 'Entf',
            disabled: isLocked,
            onClick: () => {
              if (active) {
                setObjectToDelete(active);
                setShowDeleteDialog(true);
              }
            },
          });
        }

        if (items.length === 0) return null;

        return (
          <CanvasContextMenu
            visible={contextMenu.visible}
            x={contextMenu.x}
            y={contextMenu.y}
            items={items}
            onClose={() => setContextMenu((prev) => ({ ...prev, visible: false }))}
          />
        );
      })()}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onConfirm={() => {
          if (objectToDelete) {
            const obj = objectToDelete;
            const canvas = fabricCanvasRef.current;

            // Check if it's a multi-selection (ActiveSelection)
            if (obj.type === 'activeselection' && obj._objects) {
              // Delete all objects in the selection using CompoundCommand
              const objectsToDelete = [...obj._objects];
              const deleteCommands = objectsToDelete.map(
                item => new DeleteObjectCommand(canvas, item)
              );

              const compound = new CompoundCommand(
                deleteCommands,
                `Delete ${objectsToDelete.length} objects`
              );
              commandStack.execute(compound);
            } else {
              // Single object deletion using DeleteObjectCommand
              const command = new DeleteObjectCommand(canvas, obj);
              commandStack.execute(command);
            }

            // Notify parent component
            if (onObjectDelete) {
              onObjectDelete(obj);
            }
          }
          setShowDeleteDialog(false);
          setObjectToDelete(null);
        }}
        onCancel={() => {
          setShowDeleteDialog(false);
          setObjectToDelete(null);
        }}
        title={
          objectToDelete?.type === 'activeselection'
            ? 'Auswahl löschen'
            : 'Objekt löschen'
        }
        message={
          objectToDelete?.type === 'activeselection'
            ? `Möchten Sie alle ${objectToDelete?._objects?.length || 0} ausgewählten Objekte wirklich löschen?`
            : 'Möchten Sie dieses Objekt wirklich löschen?'
        }
        confirmLabel="Löschen"
        cancelLabel="Abbrechen"
        variant="danger"
      />
    </div>
  );
};

export default GardenCanvas;
